// Aged Care Suggestion Engine Panel – strategy list, Deep Dive, SOA; generates and compares funding strategies (legacy/cashflow/pension).
// Formerly EnhancedSuggestionEngineUI; renamed from EnhancedSuggestionEngineUI for clearer UI naming.

// Add CSS animations for smooth transitions and simple bar charts
const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  /* Simple Bar Chart Styles */
  .bar-chart {
    display: flex;
    align-items: end;
    height: 220px;
    gap: 30px;
    padding: 20px 30px;
    justify-content: space-around;
    background: #f9fafb;
    border-radius: 8px;
    margin: 10px 0;
  }
  
  .bar-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    gap: 8px;
    max-width: 140px;
    min-width: 120px;
  }
  
  .bar-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;
    margin-bottom: 8px;
  }
  
  .bar {
    width: 40px;
    border-radius: 4px 4px 0 0;
    transition: height 0.3s ease-in-out;
    position: relative;
    min-height: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .bar-keep {
    background-color: #10b981;
  }
  
  .bar-pay {
    background-color: #3b82f6;
  }
  
  .bar-income-lost {
    background-color: #ef4444;
  }
  
  .bar-benefits-gained {
    background-color: #10b981;
  }
  
  .bar-label {
    font-size: 11px;
    color: #6b7280;
    text-align: center;
    font-weight: 500;
  }
  
  .bar-value {
    font-size: 10px;
    color: #374151;
    font-weight: 600;
    margin-top: 4px;
  }
  
  .mini-bar-chart {
    display: flex;
    align-items: end;
    height: 100px;
    gap: 20px;
    padding: 10px 20px;
    justify-content: center;
    background: #f9fafb;
    border-radius: 6px;
    margin: 10px 0;
  }
  
  .mini-bar {
    width: 35px;
    border-radius: 3px 3px 0 0;
    transition: height 0.3s ease-in-out;
    position: relative;
    min-height: 3px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
  
  .mini-bar-label {
    font-size: 9px;
    color: #6b7280;
    text-align: center;
    font-weight: 500;
    margin-top: 4px;
  }
  
  .mini-bar-value {
    font-size: 8px;
    color: #374151;
    font-weight: 600;
    margin-top: 2px;
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .bar-chart {
      height: 180px;
      gap: 20px;
      padding: 15px 20px;
    }
    
    .bar-group {
      max-width: 100px;
      min-width: 80px;
    }
    
    .bar {
      width: 35px;
    }
    
    .bar-label {
      font-size: 10px;
    }
    
    .bar-value {
      font-size: 9px;
    }
    
    .mini-bar-chart {
      height: 80px;
      gap: 15px;
      padding: 8px 15px;
    }
    
    .mini-bar {
      width: 30px;
    }
    
    .mini-bar-label {
      font-size: 8px;
    }
    
    .mini-bar-value {
      font-size: 7px;
    }
  }
  
  @media (max-width: 480px) {
    .bar-chart {
      height: 160px;
      gap: 15px;
      padding: 10px 15px;
    }
    
    .bar-group {
      max-width: 80px;
      min-width: 70px;
    }
    
    .bar-label {
      font-size: 9px;
    }
    
    .bar-value {
      font-size: 8px;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = animationStyles;
  document.head.appendChild(styleSheet);
}

// Proper Tooltip Component (based on App.js CalculationTooltip) - Updated to fix tax reference error
// valueLayout: 'inline' (default) = label left / value right for short values; 'stacked' = label then full-width paragraph for long copy
interface CalculationTooltipItem {
    label: string;
    value: string | number;
}

interface CalculationTooltipProps {
    title: string;
    data: CalculationTooltipItem[];
    children: React.ReactNode;
    valueLayout?: 'inline' | 'stacked';
}

const CalculationTooltip = ({ title, data, children, valueLayout = 'inline' }: CalculationTooltipProps) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState('bottom');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - wrapperRect.bottom;
            const spaceAbove = wrapperRect.top;
            const spaceRight = window.innerWidth - wrapperRect.right;
            const spaceLeft = wrapperRect.left;

            // Prefer below, but if not enough space, show above
            if (spaceBelow < tooltipRect.height + 16 && spaceAbove > tooltipRect.height + 16) {
                setPosition('top');
            } else {
                setPosition('bottom');
            }
            // If not enough space to the right, shift left
            if (spaceRight < tooltipRect.width / 2) {
                tooltipRef.current.style.left = `${window.innerWidth - tooltipRect.width - 16}px`;
            }
            // If not enough space to the left, shift right
            if (spaceLeft < tooltipRect.width / 2) {
                tooltipRef.current.style.left = `16px`;
            }
        }
    }, [show]);

    const [coords, setCoords] = useState({ top: 0, left: 0 });
    useEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const tooltipHeight = tooltipRef.current.offsetHeight || 180;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            let newPosition = position;
            // If not enough space below, show above
            if (spaceBelow < tooltipHeight + 16 && spaceAbove > tooltipHeight + 16) {
                newPosition = 'top';
            } else {
                newPosition = 'bottom';
            }
            setPosition(newPosition);
            setCoords({
                top: newPosition === 'top' ? rect.top - tooltipHeight - 16 : rect.bottom + 16,
                left: rect.left + rect.width / 2
            });
        }
    }, [show]);

    // Hooks above run every render; only branch on the data shape after they're declared.
    if (!Array.isArray(data)) return <>{children}</>;

    return (
        <div
            className="relative"
            ref={wrapperRef}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && typeof window !== 'undefined' && window.document &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            transform: 'translateX(-50%)',
                            zIndex: 10001,
                            width: valueLayout === 'stacked' ? 'min(28rem, 92vw)' : '340px',
                            maxWidth: '92vw',
                            pointerEvents: 'auto'
                        }}
                        className="p-4 bg-gray-800 text-white text-xs rounded-lg shadow-lg"
                    >
                        <h4 className="font-bold text-sm mb-3 text-blue-300">{title}</h4>
                        <div className={valueLayout === 'stacked' ? 'space-y-3' : 'space-y-1'}>
                            {data.map((item, index) => {
                                let displayValue = item.value;
                                if (typeof displayValue === 'number' && !isNaN(displayValue)) {
                                    displayValue = Math.round(displayValue).toLocaleString();
                                }
                                if (item.label.startsWith('---')) {
                                    return <hr key={`hr-${item.label}-${index}`} className="border-gray-600 my-2" />;
                                }
                                if (valueLayout === 'stacked') {
                                    return (
                                        <div key={`row-${item.label}-${index}`} className="text-left border-b border-gray-600/80 pb-3 last:border-0 last:pb-0">
                                            <div className="text-blue-200 font-semibold text-xs uppercase tracking-wide mb-1.5">{item.label}</div>
                                            <div className="text-gray-100 leading-relaxed text-[13px] whitespace-pre-line">{displayValue}</div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={`row-${item.label}-${index}`} className="flex justify-between gap-3">
                                        <span className="text-gray-400 shrink-0">{item.label}:</span>
                                        <span className="font-mono font-semibold text-right break-words">{displayValue}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

import { engineRowPieces } from './engineRowWords.ts';
import { engineOpening, remainingFloorMs, type EngineOpening } from './engineOpening.ts';
import { moneyOrderParagraph, type RunwayRow } from './moneySequence.tsx';
import { analysisCacheKey, cachedAnalysis, rememberAnalysis } from './engineAnalysisCache.ts';
import { scenarioLivingExpensesOr } from '../../../../shared/src/calculation/support/livingExpenses.ts';
import { radLevelDedupeKey } from './radLevelKey.ts';
import { anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';
import { householdDailyCost, strategyHouseholdDailyCost, strategyPhaseTwoDailyCost } from './householdCareCosts.ts';
import { phaseFromLabel, recapPhaseLine, roleNames } from './carePhases.ts';
import { describeFundingKeys } from './fundingLabels.ts';
import { homeCanBeLet } from '../../../../shared/src/calculation/householdPosition.ts';
import { runwayFactsOf } from './shortfallOutlook.ts';
import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import AnnuityPreferenceGate from '../../components/strategy/AnnuityPreferenceGate.tsx';
import { annuityPreferenceOf, type AnnuityAppetite, type AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import { SameAsBestEstateNote } from '../../components/strategy/SameAsBestEstateNote.tsx';
import { pickCashflowCoveredLeader, cashflowCoveredNote, CASHFLOW_COVERED_LABEL, CASHFLOW_COVERED_CHIP, BEST_ESTATE_CHIP, GOAL_CHIP_LABELS, friendlyGoalLabel, coveredIsBestEstate, SAME_PLAN_NOTE } from '../../strategy/cashflowCoveredGoal.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, MoneyFlow } from '../../components/narrative/PlainWords.tsx';
import StrategyPlanRecap, { buildPlanRecapClauses, RecapClauses } from './StrategyPlanRecap.tsx';
import { AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import PlanExplanationModal from './PlanExplanationModal.tsx';
import type { AgedCareBaseline } from './agedCareBaseline.ts';
import { BookOpen, ShieldAlert } from 'lucide-react';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromAgedCareStrategy } from '../../strategy/strategyRisks.ts';
import { createPortal } from 'react-dom';
import { fundingRunwayCopy, type FundingProjectionYear, planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { payableDebtsOf, type PayableDebt } from './DebtPayoffStrategy.tsx';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import AnnualSnapshot from './AnnualSnapshot.tsx';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { getPersonalLiquidAssetsForRad, computeRadFundingTotals, findAssetInFactFind } from '../../utils/entityFundingUtils.ts';
import { calculateSharePartialDisposalTax } from '../../../../shared/src/calculation/entityDisposalTax.ts';
import { parseRadFundingAssetKey } from '../../../../shared/src/calculation/support/entityFundingUtils.ts';
import { flattenSeRadFundingToManualKeys } from '../../utils/suggestionEngineFunding.ts';
import { AGED_CARE_SCHEDULE } from '../../../../shared/src/calculation/constants/agedCareSchedule.ts';
import { engineStrategyHorizonWarning, type HorizonSetAside } from './strategyHorizonFlags.ts';
import StampDutyFields from '../../components/ui/StampDutyFields.tsx';
import {
    Calculator,
    ArrowLeft, AlertTriangle, CheckCircle,
    BarChart3, HelpCircle, Settings, X, FileText
} from 'lucide-react';
import {
    canCommenceAbpFromDownsizer,
    isDownsizerContributionEligible,
    DOWNSIZER_MIN_AGE,
} from '../../../../shared/src/calculation/downsizerContributionUtils.ts';
import { DOWNSIZER_CONTRIBUTION_CAP } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { calculateAge, formatCurrency } from '../../utils/index.ts';
import { getRecommendedEmergencyFund, MIN_EMERGENCY_FUND } from '../../utils/emergencyFund.ts';
import { AgedCareSuggestionEngineDeepDive } from './AgedCareSuggestionEngineDeepDive.tsx';

// AgedCareSuggestionEngineDeepDive.tsx's own prop interface isn't exported (internal to that module),
// and this panel's Strategy/AnalysisResult types are a slightly different (but structurally
// equivalent) loose shape from that file's DeepDiveScenario/DeepDiveAnalysis — widen the reference
// here rather than duplicating/exporting an interface just for this call site.
const DeepDiveModal = AgedCareSuggestionEngineDeepDive as unknown as React.ComponentType<Record<string, unknown>>;
import { getRadFundingTooltipData } from './getRadFundingTooltipData.tsx';
import type { RadFundingBag } from './getRadFundingTooltipData.tsx';
import { computeFutureEventAdjustedLegacy, pickLowerRadNearBestStrategy, estimateRadPaidForStrategy, formatRadStrategyDisplayName } from './futureLifeEventUtils.tsx';
import type { EvaluatedStrategy } from './futureLifeEventUtils.tsx';
import {
    SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD,
    LOWER_RAD_NEAR_BEST_LEGACY_BADGE,
} from '../../constants/insightsConfig.ts';
import {
    appendEntityIpSaleScenarios,
    applySuggestionEngineScenarioToFactFind,
    calculateSeRadFundingForAmount,
    generateSeFundingVariants,
    isStructureFundingIncludedInSuggestions,
} from '../../utils/suggestionEngineFunding.ts';
import type { RadFundingScenario } from '../../utils/suggestionEngineFunding.ts';
import { appendMeansTestStrategyScenarios } from '../../utils/agedCareMeansTestCandidates.ts';
import { appendEquityReleaseScenarios } from '../../utils/agedCareEquityReleaseCandidates.ts';
import { stripFundingOrderSuffix } from './futureLifeEventUtils.tsx';
import { StructureFundingInSuggestionsFields } from './StructureFundingInSuggestionsFields.tsx';
import {
    buildAgedCareDigitalSOASnapshot,
    openAgedCareDigitalSOAAttachment
} from './AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import { GradientText, Button, CurrencyInput } from '../../components/ui/index.tsx';
import {
    formatServiceBuilderSelectionId,
    notifyHomeCareStrategiesUpdated,
    setActiveHomeCareStrategyId,
} from '../../strategy/careCompanionStrategyEvents.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { AgedCareCosts } from '../../../../shared/src/calculation/agedCareCostTypes.ts';

// DraftFactFind is structurally compatible with FactFind (formDraft.ts); the shared
// calc-engine fns below defensively coerce internally regardless (factFindTypes.ts header).
const asFactFind = (factFind: DraftFactFind): FactFind => factFind as unknown as FactFind;
import type { FactFindAsset, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';

type DraftAsset = FactFindAsset;

/**
 * A suggestion-engine strategy/scenario card — the calc engine's `evaluate*`/`generateAllStrategies`
 * family (see calculationApi.ts) returns these as `Promise<any>`, and this panel is the one place that
 * both builds them locally (generateScenarios etc.) and hands them to the Deep Dive / Digital SOA
 * modules, so it stays a single loose "kitchen sink" type (index signature + the fields this file
 * actually reads) rather than duplicating a slightly-different interface per consumer.
 */
export interface Strategy {
    name?: string;
    type?: string;
    /**
     * The means-test lever layered on this strategy, where it has one.
     *
     * Carried out of the engine so the client-side comparisons can weigh what a strategy COMMITS,
     * not merely what it deposits — see estimateLockedCapitalForStrategy.
     */
    meansTestStrategy?: {
        type?: string;
        amount?: number;
        purchaseAmount?: number;
        [key: string]: unknown;
    } | null;
    /** The equity-release loan this strategy takes, with what the projection says it grows to. */
    equityRelease?: Record<string, unknown> | null;
    isLowerRadNearBestCard?: boolean;
    isFutureEventOverlayCard?: boolean;
    groupKey?: string | number;
    /** Present when this object is actually the session-storage "saved strategy" wrapper (see
     *  saveSelectedStrategy) rather than a raw suggestion-engine strategy — the real strategy lives here. */
    strategy?: Strategy;
    snapshot?: {
        legacyValue?: number;
        netCashflow?: number;
        pension?: number;
        totalAgedCareCosts?: number;
        investmentIncome?: number;
        livingExpenses?: number;
        homeStatus?: string;
        agePensionBreakdown?: { label: string; value: string | number }[];
        abpIncome?: number;
        rentalIncome?: number;
        otherInvestmentIncome?: number;
        agedCareBreakdown?: { label: string; value: string | number }[];
        taxBreakdown?: { label: string; value: string | number }[];
        taxPayable?: number;
        [key: string]: unknown;
    };
    projections?: Record<string, unknown>[];
    radFunding?: RadFundingBag;
    radAmount?: number;
    radPaid?: number;
    radPaidNet?: number;
    strategyOptions?: Record<string, unknown>;
    scenarioConfig?: Record<string, unknown>;
    strategySpecificAgedCareCosts?: Record<string, unknown>;
    context?: string;
    description?: string;
    assetAllocation?: {
        assetName?: string;
        status?: 'sold' | 'partiallySold' | 'retained' | string;
        remainingAmount?: number | string;
        liquidatedAmount?: number | string;
        [key: string]: unknown;
    }[];
    originalFactFind?: DraftFactFind;
    optimisationLabel?: string;
    radRoomPrice?: number;
    // Ad hoc top-level fallbacks some strategy sources set instead of (or alongside) `snapshot`.
    agePension?: number;
    estimatedAgePension?: number;
    cashFlow?: number;
    cashflow?: number;
    legacyValue?: number;
    projectedEstateValue?: number;
    // Future-life-event overlay (computeFutureEventAdjustedLegacy) — see futureLifeEventUtils.tsx.
    futureAdjustedLegacy?: number;
    futureEventAdjustmentDelta?: number;
    futureEventAssumptions?: {
        partnerEntryYear?: number;
        partnerRoomPrice?: number;
        radPct?: number;
        originalEntrantAliveAtEvent?: boolean;
        yearsAffected?: number;
        additionalAnnualCareCost?: number;
        additionalLifecycleCost?: number;
        releasedRadFromInitialEntrant?: number;
        [key: string]: unknown;
    };
    futureEventMeta?: {
        bandUsd?: number;
        baselineLegacy?: number;
        maxAdjustedLegacy?: number;
        selectedAdjustedLegacy?: number;
        topAdjustedStrategyName?: string;
        selectedStrategyName?: string;
        radPaidSelected?: number;
        radPaidTopAdjusted?: number;
        usedLowerRadTieBreak?: boolean;
        [key: string]: unknown;
    };
    // Display-only badges/labels stitched on by this panel's card-building logic.
    recommendationLabels?: string[];
    recommendedLabel?: string;
    lowerRadNearBestMeta?: {
        maxLegacy: number;
        bandUsd: number;
        topLegacyStrategyName: string;
        radPaidThis: number;
        radPaidTop: number;
    };
    [key: string]: unknown;
}

/** Match displayed suggestion-engine cards when the same strategy name appears more than once (e.g. optimal vs lower RAD). */
function strategyCardSelectionKey(s: Strategy | null | undefined): string {
    if (!s?.name) return '';
    return [
        String(s.name),
        s.isLowerRadNearBestCard ? '1' : '0',
        s.isFutureEventOverlayCard ? '1' : '0',
        s.groupKey != null ? String(s.groupKey) : '',
    ].join('\0');
}

export interface SuggestionEngineSettings {
    legacyYears: number;
    emergencyFund: number;
    newHomeValue: number;
    annualRentCost: number;
    sellingCosts: number | undefined;
    stampDuty?: number;
    newHomeState?: string;
    partnerLivingArrangement: 'remain_in_home' | 'rent_separately' | 'downsize_together';
    enablePartnerFutureEvent: boolean;
    partnerEntryYear: number;
    partnerFutureRoomPrice: number | undefined;
    originalEntrantAliveAtPartnerEntry: boolean;
    structureFundingInSuggestions?: 'include' | 'exclude';
}

interface SettingsFormProps {
    currentSettings: SuggestionEngineSettings;
    onSave: (settings: SuggestionEngineSettings) => void;
    onCancel: () => void;
    factFind: DraftFactFind;
    scenarioLivingExpenses?: number | string;
}

// Settings Form Component
const SettingsForm = ({ currentSettings, onSave, onCancel, factFind, scenarioLivingExpenses = 0 }: SettingsFormProps) => {
    const [settings, setSettings] = useState<SuggestionEngineSettings>(currentSettings);
    const [structureFundingInSuggestions, setStructureFundingInSuggestions] = useState(
        () => factFind?.structureFundingInSuggestions || 'exclude'
    );
    const hasStructures = (factFind?.structures || []).length > 0;
    const annualLivingForGuidance = useMemo(
        () => parseFloat(String(scenarioLivingExpenses)) || 0,
        [scenarioLivingExpenses]
    );
    const sixMonthGuidance = useMemo(() => Math.max(MIN_EMERGENCY_FUND, Math.round(annualLivingForGuidance / 2)), [annualLivingForGuidance]);
    const partnerName = (factFind?.partner?.firstName || '').trim() || 'your partner';
    const clientName = (factFind?.client?.firstName || '').trim() || 'the person entering care first';
    /** Both of a couple entering care: the home is sold, not downsized, and there is nobody to house. */
    const bothInCareTogether = (factFind?.goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'both';

    const handleSave = () => {
        onSave({
            ...settings,
            ...(hasStructures ? { structureFundingInSuggestions } : {}),
        });
    };

    return (
        <div className="space-y-6">
            {/* Legacy Years */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Legacy Years
                </label>
                <input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.legacyYears}
                    onChange={(e) => setSettings({...settings, legacyYears: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span className="font-medium">{settings.legacyYears} years</span>
                    <span>10 years</span>
                </div>
            </div>

            {/* Emergency Fund Protection */}
            <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-green-400">
                <div className="flex items-center mb-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                    </div>
                    <h4 className="font-semibold text-gray-800">Emergency Fund Protection</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">How much do you wish to retain for emergency funds?</p>
                <CurrencyInput
                    label="Emergency Fund Amount ($)"
                    value={settings.emergencyFund ?? ''}
                    onChange={(v) => setSettings({...settings, emergencyFund: v ?? 0})}
                    placeholder="Enter emergency fund amount"
                />
                <div className="mt-3 p-3 bg-white border border-slate-200/70 border-l-4 border-green-400 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Recommended minimum:</span>
                        <span className="font-semibold text-green-600">{formatCurrency(sixMonthGuidance)}</span>
                    </div>
                    <p className="text-xs text-gray-500">
                        As a rule of thumb, the greater of <strong>6 months of living expenses</strong> ({formatCurrency(annualLivingForGuidance)} ÷ 2) or <strong>{formatCurrency(MIN_EMERGENCY_FUND)}</strong> — a retiree&apos;s emergencies tend to be lumpy one-off costs. Keep a higher amount if you prefer; whatever you set is what the engine uses.
                    </p>
                    <button
                        type="button"
                        onClick={() => setSettings({ ...settings, emergencyFund: sixMonthGuidance })}
                        className="mt-2 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                    >
                        Use this amount
                    </button>
                </div>
            </div>

            {/* Alternative Scenario Parameters */}
            <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center mb-3">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        <div className="w-3 h-3 bg-gray-600 rounded-full"></div>
                    </div>
                    <h4 className="font-semibold text-gray-800">What happens to the family home</h4>
                </div>
                {/*
                  * The same heading and intro as the Strategy Analysis Setup modal, which asks for
                  * these three figures first (owner, 23 Sep 2026). Two screens asking for the same
                  * numbers under different words read as two different questions.
                  */}
                <p className="text-sm text-blue-600 mb-4">
                    {bothInCareTogether
                        ? `With ${clientName} and ${partnerName} both in care, nobody is left living in the family home. We model keeping it, letting it out, and selling it — the selling costs below price the sale. Rough numbers are fine.`
                        : factFind?.adviceFor === 'couple'
                        ? `When ${clientName} moves into care, ${partnerName} stays in the family home — unless it is sold or let out. We model all three; these figures price the last two. Rough numbers are fine.`
                        : `With ${clientName} in care, the family home can be kept, sold, or let out. We model all three; these figures price the last two. Rough numbers are fine.`}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Both in care together: there is no new home to price and nobody to house. */}
                    {!bothInCareTogether && (
                    <div>
                        <CurrencyInput
                            label="New home value (if downsizing)"
                            value={settings.newHomeValue ?? ''}
                            onChange={(v) => setSettings({...settings, newHomeValue: v ?? 0})}
                            placeholder="Enter new home value"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Estimated purchase price for downsized home
                        </p>
                    </div>
                    )}
                    {!bothInCareTogether && (
                    <StampDutyFields
                        state={settings.newHomeState}
                        onStateChange={(v) => setSettings({...settings, newHomeState: v})}
                        newHomePrice={settings.newHomeValue}
                        duty={settings.stampDuty}
                        onDutyChange={(v) => setSettings({...settings, stampDuty: v})}
                    />
                    )}
                    {/* Couples only: this prices where the PARTNER lives once the home is let out, and
                        a single client has nobody to house (owner-anticipated, 23 Sep 2026). The setup
                        modal's copy of these fields was already couple-gated; this one was not. */}
                    {factFind?.adviceFor === 'couple' && !bothInCareTogether && (
                        <div>
                            <CurrencyInput
                                label={`Annual rent cost (if ${partnerName} rents elsewhere)`}
                                value={settings.annualRentCost ?? ''}
                                onChange={(v) => setSettings({...settings, annualRentCost: v ?? 0})}
                                placeholder="Enter annual rent cost"
                            />
                        </div>
                    )}
                    <div>
                        <CurrencyInput
                            label="Selling costs ($)"
                            value={settings.sellingCosts ?? ''}
                            onChange={(v) => setSettings({...settings, sellingCosts: v})}
                            placeholder="e.g. 75000"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Estimated selling costs in dollars
                        </p>
                    </div>
                </div>
            </div>

            {/* Future Life Event (partner enters care later) — PARTNERED CLIENTS ONLY.
                Offering to model "if your partner enters care later" to someone who has no partner
                is the same mistake as the goal-tab copy, and a client meeting on 2026-08-21 caught it. */}
            {factFind?.adviceFor === 'couple' && (
            <div className="p-4 bg-white rounded-lg border border-slate-200/70 border-l-4 border-violet-400 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h4 className="font-semibold text-violet-950">Future life events (optional)</h4>
                        <p className="text-sm text-violet-900/90 mt-1">
                            Conditional scenario if {partnerName} may enter residential care later.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setSettings({ ...settings, enablePartnerFutureEvent: !settings.enablePartnerFutureEvent })}
                        className={`px-3 py-1.5 rounded text-sm font-semibold transition-colors ${
                            settings.enablePartnerFutureEvent
                                ? 'bg-violet-700 text-white hover:bg-violet-800'
                                : 'bg-white text-violet-900 border border-violet-300 hover:bg-violet-50'
                        }`}
                    >
                        {settings.enablePartnerFutureEvent ? 'Enabled' : 'Disabled'}
                    </button>
                </div>

                {settings.enablePartnerFutureEvent && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start">
                                <div className="flex min-w-0 flex-col">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">
                                        Approximate year {partnerName} enters care
                                    </label>
                                    <p className="mb-2 min-h-[2.5rem] text-xs leading-snug text-gray-500">
                                        Projection year from today (1 = first year; aligns with your projection horizon).
                                    </p>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={settings.partnerEntryYear ?? 3}
                                        onChange={(e) =>
                                            setSettings({
                                                ...settings,
                                                partnerEntryYear: Math.max(1, parseInt(e.target.value, 10) || 1),
                                            })
                                        }
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                                    />
                                </div>
                                <div className="flex min-w-0 flex-col">
                                    <label className="mb-2 block text-sm font-medium text-gray-700">
                                        Second room price for {partnerName} ($)
                                    </label>
                                    <p className="mb-2 min-h-[2.5rem] text-xs leading-snug text-gray-500">
                                        Defaults to the current facility accommodation price if left blank when saving.
                                    </p>
                                    <CurrencyInput
                                        label=""
                                        value={settings.partnerFutureRoomPrice ?? ''}
                                        onChange={(v) => setSettings({ ...settings, partnerFutureRoomPrice: v ?? 0 })}
                                        placeholder="Facility accommodation price"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <label className="mb-1 block text-sm font-medium text-gray-800">
                                {clientName} at {partnerName}&apos;s entry date
                            </label>
                            <p className="mb-2 text-xs text-gray-600">
                                Affects whether an earlier refundable deposit is assumed available to fund {partnerName}&apos;s room in this illustration.
                            </p>
                            <details className="group mb-5 text-xs text-gray-600">
                                <summary className="flex cursor-pointer list-none items-center gap-1 pb-1 font-medium text-violet-700">
                                    <span className="underline decoration-violet-300 underline-offset-2 group-open:no-underline">
                                        Why we ask
                                    </span>
                                </summary>
                                <p className="mb-1 mt-2 border-l-2 border-violet-200 pl-3 leading-relaxed text-gray-600">
                                    A refundable accommodation deposit stays linked to the resident who paid it until their arrangement ends. If {clientName} is still living when {partnerName} enters care, we do not assume that deposit is available for {partnerName}&apos;s admission. If {clientName} has died before {partnerName} enters care, this model assumes any refundable amount still due back may reduce funding needed for {partnerName}&apos;s room. Timing, provider agreements, and estate matters vary — this is a simple illustration only, not a guarantee. Always confirm details with your aged care provider.
                                </p>
                            </details>
                            <div className="mt-2 grid w-full grid-cols-2 gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setSettings({ ...settings, originalEntrantAliveAtPartnerEntry: true })}
                                    className={`min-h-[42px] rounded-lg border px-3 py-2.5 text-center text-sm font-medium ${
                                        settings.originalEntrantAliveAtPartnerEntry
                                            ? 'border-violet-300 bg-violet-100 text-violet-900 shadow-sm'
                                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    Still alive
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSettings({ ...settings, originalEntrantAliveAtPartnerEntry: false })}
                                    className={`min-h-[42px] rounded-lg border px-3 py-2.5 text-center text-sm font-medium ${
                                        !settings.originalEntrantAliveAtPartnerEntry
                                            ? 'border-violet-300 bg-violet-100 text-violet-900 shadow-sm'
                                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    Has passed
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            )}

            {hasStructures && (
                <StructureFundingInSuggestionsFields
                    value={structureFundingInSuggestions}
                    onChange={setStructureFundingInSuggestions}
                />
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold"
                >
                    Save & Re-optimise
                </button>
            </div>
        </div>
    );
};

export interface CoupleValidation {
    exploreAlternatives?: boolean;
    downsizeNewHomeValue?: number;
    partnerAnnualRent?: number;
    enablePartnerFutureEvent?: boolean;
    partnerEntryYear?: number;
    partnerFutureRoomPrice?: number;
    originalEntrantAliveAtPartnerEntry?: boolean;
    sellingCosts?: number;
    /** The duty on the new home and the state it is in — estimated on the setup modal, overwritable. */
    stampDuty?: number;
    newHomeState?: string;
    emergencyFund?: number;
    [key: string]: unknown;
}

interface RecommendedOptimalStrategyRow {
    label: string;
    strategy: Strategy;
    /** For the cashflow-covered goal: which of its three cases chose this leader (cashflowCoveredGoal.ts). */
    cashflowCovered?: import('../../strategy/cashflowCoveredGoal.ts').CashflowCoveredVerdict;
}

interface FutureEventV2Config {
    enabled: boolean;
    rankingMetric: string;
    fourthCardMetric?: string;
    assumptions: {
        partnerEntryYear: number;
        partnerRoomPrice: number;
        originalEntrantAliveAtEvent: boolean;
    } | null;
}

/**
 * The suggestion engine's analysis result — assembled locally in this panel from
 * calculationApi.ts's evaluateAgedCareRadFundingScenarios (a `Promise<any>` REST call by design), so
 * kept as a real-but-loose interface rather than a strict model.
 */
export interface AnalysisResult {
    strategies: Strategy[];
    allSimulatedStrategies: Strategy[];
    totalScenarios: number;
    processedScenarios: number;
    recommendedOptimalStrategies: RecommendedOptimalStrategyRow[];
    /**
     * Strategies that would have won a ranking but were set aside because they need more time than
     * the client's age and health support (strategyHorizonFlags.ts). Disclosed under the leader card
     * and in the advice document, and openable — never hidden, never promoted (owner, 23 Sep 2026).
     */
    horizonSetAside?: HorizonSetAside<Strategy>[];
    futureEventV2: FutureEventV2Config;
}

type SuggestionEngineGoals = NonNullable<DraftFactFind['goals']>;

interface AgedCareSuggestionEnginePanelProps {
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    agedCareCosts: AgedCareCosts;
    goals?: SuggestionEngineGoals;
    onStrategySelect: (strategy: Strategy) => void;
    selectedStrategy?: Strategy | null;
    legacyYears?: number;
    coupleValidation?: CoupleValidation;
    onScenarioSaved?: (strategy: Strategy) => void;
    onNavigateToDashboard?: () => void;
    /**
     * The engine's own "Do Nothing" row whenever an analysis is on screen (null when there is none),
     * so the analyser's rail and these cards measure against ONE run (owner, 9 Oct 2026: a card
     * read "$82,080 behind" a walk-in figure its own run never produced).
     */
    onDoNothingRow?: (row: Strategy | null) => void;
    /**
     * The id of a saved plan being re-opened, when that is why this panel mounted (null for a fresh
     * run). Decides the opening — engineOpening.ts: a reopen is quiet, a fresh run thinks.
     */
    reopenedSavedStrategyId?: string | null;
    /**
     * The selected plan's long-run projection rows (the rail's runway), so Explain this on the
     * selected card can say the order the money is used over the years tested rather than only
     * over the plan period. Null when no card is selected.
     */
    selectedPlanRunwayRows?: RunwayRow[] | null;
    /**
     * The shared strategy-options bag the Aged Care Analyser maintains. Only `careStartDate` is read
     * from it here, and only to project every candidate against the same entry date the manual
     * strategy uses — ranking scenarios that assumed different admission dates would compare nothing.
     */
    strategyOptions?: Record<string, unknown>;
    /**
     * The no-plan baseline, computed once by the analyser (agedCareBaseline.ts) and passed down.
     *
     * "Explain this" on a strategy card reconciles that strategy AGAINST this benchmark. Passed
     * rather than rebuilt on purpose: two computations of the same baseline is precisely how the
     * Financial Impact panel and the plan summary came to disagree by $97,649, with neither half's
     * unit test able to see it. Null when the analyser could not compute one, in which case the
     * explanation shows the plan's own figures without a comparison rather than inventing one.
     */
    agedCareBaseline?: AgedCareBaseline | null;
}


/**
 * The one line a client actually reads on a strategy card: when the savings run low, and what pays
 * for the care after that.
 *
 * Built from the strategy's own projections — the same rows the Deep Dive charts — so it cannot
 * drift from the year-by-year figures. Deliberately calm: amber only when the plan outlives what the
 * savings cover, because on most strategies this is reassuring news and should read that way.
 */

/**
 * The line under the goal tabs, which must not describe rows this client can never see.
 *
 * A single client was shown "optional lower-RAD + partner-later rows when enabled" during a meeting
 * on 2026-08-21. He has no partner: the partner-later card cannot appear for him, and offering it
 * invites him to look for something that does not exist. Exported so the rule is pinned by a test
 * rather than by whoever next edits the JSX.
 */
export const legacyFocusHint = (isCouple: boolean): string =>
    isCouple
        ? 'Estate focus; optional lower-RAD + partner-later rows when enabled.'
        : 'Estate focus; an optional lower-RAD row appears when a similar estate can be reached with less paid up front.';
const StrategyFundingNote = ({ strategy }: { strategy: EvaluatedStrategy }) => {
    const copy = fundingRunwayCopy((strategy.projections ?? []) as FundingProjectionYear[]);
    const netCashflow = Number(strategy.snapshot?.netCashflow ?? 0);

    // No projections to read (an older saved strategy, or a scenario that failed to project): say the
    // little that is certainly true rather than inventing a runway.
    if (!copy) {
        return (
            <p className="text-sm text-gray-600 text-center mb-3">
                {netCashflow < 0 ? 'Any shortfall is met by drawing on savings.' : 'Positive cashflow position maintained.'}
            </p>
        );
    }

    /* White card with a coloured spine rather than a pastel fill: the fill read as dated, and a
       block of yellow shouts at a reader who mostly needs the calm version of this message. The
       amber lives in the spine and one small icon; the words stay on white. */
    return (
        <div className={`mb-3 rounded-xl border border-slate-200/70 bg-white shadow-sm border-l-4 ${copy.flag ? 'border-l-amber-500' : 'border-l-emerald-500'} px-3.5 py-3`}>
            <p className="text-sm text-slate-700 leading-relaxed">{copy.headline}</p>
            {copy.flag && (
                <p className="mt-1.5 flex items-start gap-1.5 text-sm text-slate-600 leading-relaxed">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-500" />
                    <span>{copy.flag}</span>
                </p>
            )}
        </div>
    );
};
const AgedCareSuggestionEnginePanel = ({
    factFind,
    setFactFind,
    agedCareCosts,
    goals = {},
    onStrategySelect,
    selectedStrategy,
    legacyYears = 5,
    coupleValidation = { downsizeNewHomeValue: 500000, partnerAnnualRent: 40000 },
    onScenarioSaved,
    onNavigateToDashboard,
    onDoNothingRow,
    // Declared on the props but previously unread here. Now the source of the care start date every
    // candidate is projected against — see `careStartDateFromStrategyOptions`.
    strategyOptions,
    /*
     * The no-plan baseline, computed once by the analyser and passed down rather than rebuilt here.
     * "Explain this" reconciles a strategy AGAINST it, and a second computation of the same
     * benchmark is exactly how the panel and the summary came to disagree by $97,649 once before.
     */
    agedCareBaseline = null,
    reopenedSavedStrategyId = null,
    selectedPlanRunwayRows = null,
}: AgedCareSuggestionEnginePanelProps) => {
    // Decided once, at mount; back to 'thinking' after the first run so re-runs show the work.
    const openingRef = useRef<EngineOpening>(engineOpening({ reopenedSavedStrategyId }));
    /** Which strategy's plain-words explanation is open, if any. */
    const [explainStrategy, setExplainStrategy] = useState<any>(null);
    // "What could go wrong" for one card (StrategyRisksPanel; the list is strategyRisks.ts).
    const [riskStrategy, setRiskStrategy] = useState<Strategy | null>(null);

    /**
     * The walk-in position under every snapshot tile — the SAME no-plan baseline this page's rail
     * measures against, read once, so a card can say "$64,360, up $15,678" without the rail having
     * to repeat the plan beside it (owner, 2026-09-22). Null when the baseline could not be run, in
     * which case the tiles simply show the plan's figures.
     */
    const snapshotBaseline = useMemo(() => {
        const fin = agedCareBaseline?.financials as { investmentIncome?: number; netTax?: number; cashFlowBreakdown?: Array<{ label?: string; value?: unknown }> } | null | undefined;
        if (!fin) return null;
        const livingRow = fin.cashFlowBreakdown?.find((r) => typeof r.label === 'string' && r.label.includes('Living Expenses'));
        const costs = agedCareBaseline?.agedCareCosts as { totalDailyCost?: number } | null | undefined;
        const secondCosts = agedCareBaseline?.secondAgedCareCosts as { totalDailyCost?: number } | null | undefined;
        return {
            pension: Number(agedCareBaseline?.pension) || 0,
            investmentIncome: Number(fin.investmentIncome) || 0,
            livingExpenses: livingRow && typeof livingRow.value === 'number' ? Math.abs(livingRow.value) : Number(factFind?.expenses?.livingExpenses) || 0,
            // The first year: the second room only where both enter together (the baseline's second stage is its own figure).
            careCosts: householdDailyCost(costs, agedCareBaseline?.secondEntersLater ? null : secondCosts) * 365,
            taxPayable: Number(fin.netTax) || 0,
            netCashflow: Number(agedCareBaseline?.cashFlowAfterCare) || 0,
        };
    }, [agedCareBaseline, factFind]);
    // Shared with the digital SOA (see futureLifeEventUtils) so the card headline and the SOA never disagree.
    const getDisplayStrategyName = (name: string = '', radPaidNet: number | null = null, roomPrice: number | null = null, secondRoom: { radPaid: number; roomPrice: number } | null = null, move: { stayPut: boolean; fromRefund: number } | null = null, village: { fromVillage: number } | null = null) =>
        formatRadStrategyDisplayName(name, radPaidNet, roomPrice, secondRoom, move, village);
    /** Leaving a village, as the row records it, for the name. */
    const villageOf = (row: unknown): { fromVillage: number } | null => {
        const ve = (row as { villageExit?: { fromVillage?: number } | null } | null | undefined)?.villageExit;
        return ve ? { fromVillage: Number(ve.fromVillage) || 0 } : null;
    };
    /** A change of home, as the row records it, for the name. */
    const moveOf = (row: unknown): { stayPut: boolean; fromRefund: number } | null => {
        const mv = (row as { facilityMove?: { stayPut?: boolean; fromRefund?: number } | null } | null | undefined)?.facilityMove;
        return mv ? { stayPut: !!mv.stayPut, fromRefund: Number(mv.fromRefund) || 0 } : null;
    };
    /** The second room a row carries, where both of a couple are in care, for the name and the words. */
    const secondRoomOf = (row: unknown): { radPaid: number; roomPrice: number; name: string; leadName: string } | null => {
        const sr = (row as { secondRoom?: { radPaid?: number; roomPrice?: number; person?: 'client' | 'partner' } | null } | null | undefined)?.secondRoom;
        if (!sr || !((Number(sr.roomPrice) || 0) > 0)) return null;
        const names = roleNames(factFind, sr.person);
        return { radPaid: Number(sr.radPaid) || 0, roomPrice: Number(sr.roomPrice) || 0, name: names.secondName, leadName: names.firstName };
    };
    /** The second room a row carries, in full, for the allocation grid and the card's strip. */
    const secondRoomRowOf = (row: unknown) =>
        (row as { secondRoom?: { person?: 'client' | 'partner'; roomPrice?: number; radPaid?: number; radFunding?: Record<string, number>; entersLater?: boolean; careStartDate?: string; agedCareCosts?: { dailyAccommodationPayment?: number } } | null } | null | undefined)?.secondRoom ?? null;
    const partnerDisplayName = useMemo(
        () => (factFind?.partner?.firstName || '').trim() || 'your partner',
        [factFind?.partner?.firstName]
    );
    const clientDisplayName = useMemo(
        () => (factFind?.client?.firstName || '').trim() || 'the person entering care first',
        [factFind?.client?.firstName]
    );
    const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
    // The run's own no-plan row, up to the analyser (see the prop). Null again when the analysis clears.
    useEffect(() => {
        const rows = (analysis?.allSimulatedStrategies?.length ? analysis.allSimulatedStrategies : analysis?.strategies) || [];
        const row = rows.find((s) => /^(do nothing|stay put)$/i.test(String(s?.name || '').trim())) || null;
        onDoNothingRow?.(row);
        return () => { onDoNothingRow?.(null); };
    }, [analysis, onDoNothingRow]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deepDiveModal, setDeepDiveModal] = useState(false);
    const [selectedScenario, setSelectedScenario] = useState<Strategy | null>(null);
    const [activeTab, setActiveTab] = useState('yearlyProjections');
    const [selectedStrategyForSOA, setSelectedStrategyForSOA] = useState<Strategy | null>(null);
    const [showSOAView, setShowSOAView] = useState(false);
    const [showAllStrategiesTable, setShowAllStrategiesTable] = useState(false);
    /** Side-by-side of recommendation leaders only (legacy / cashflow / pension + optional lower RAD + future event) — not the full simulation list. */
    const [showGoalLeadersModal, setShowGoalLeadersModal] = useState(false);
    const [allStrategiesSortBy, setAllStrategiesSortBy] = useState('legacy');
    /*
     * A REAL QUOTE, and the draft being typed, kept apart.
     *
     * Only the committed value re-runs the engine - typing a digit must not fire an analysis per
     * keystroke. Same split the village module uses.
     */
    const [acAnnuityQuote, setAcAnnuityQuote] = useState<number | null>(null);
    const [acAnnuityQuoteDraft, setAcAnnuityQuoteDraft] = useState<number | string>('');
    const [showAssumptionsModal, setShowAssumptionsModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    /** User-facing filter: which single objective to surface first. 'all' uses the same card set as legacy (v1). */
    const [optimisationFocus, setOptimisationFocus] = useState(() => {
        const g = goals?.agedCareSuggestionFocus;
        if (g === 'cashflow' || g === 'pension' || g === 'legacy' || g === 'all' || g === 'covered') return g;
        // Best estate leads (owner, 24 Sep 2026: "Best estate, then Best estate cashflow covered…").
        return 'legacy';
    });
    const [showSaveWarning, setShowSaveWarning] = useState(false);
    const [currentSettings, setCurrentSettings] = useState<SuggestionEngineSettings>({
        legacyYears: legacyYears || 5,
        emergencyFund: getRecommendedEmergencyFund(factFind), // Default: greater of 6 months' expenses or the floor
        // Alternative Scenario Parameters (from original setup)
        newHomeValue: Math.round((parseFloat(String(factFind?.home?.value)) || 0) * 0.7) || 250000, // ~70% of current home (if downsizing)
        annualRentCost: 40000, // If partner rents
        sellingCosts: 0, // Selling costs in $ for downsize (consistent with Manual Control)
        // Partner living arrangement (for couples)
        partnerLivingArrangement: 'remain_in_home', // 'remain_in_home', 'rent_separately', 'downsize_together'
        // Future life event (optional): partner enters residential care later.
        enablePartnerFutureEvent: factFind?.adviceFor === 'couple',
        partnerEntryYear: 3,
        partnerFutureRoomPrice: parseFloat(String(agedCareCosts?.radAmount)) || 500000,
        originalEntrantAliveAtPartnerEntry: true,
    });
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    // Keep Suggestion Engine settings aligned with Strategy Analysis Setup (coupleValidation)
    useEffect(() => {
        if (!coupleValidation) return;
        setCurrentSettings((prev) => ({
            ...prev,
            enablePartnerFutureEvent:
                coupleValidation.enablePartnerFutureEvent !== undefined
                    ? coupleValidation.enablePartnerFutureEvent
                    : prev.enablePartnerFutureEvent,
            partnerEntryYear: coupleValidation.partnerEntryYear ?? prev.partnerEntryYear,
            partnerFutureRoomPrice:
                (coupleValidation.partnerFutureRoomPrice || 0) > 0
                    ? coupleValidation.partnerFutureRoomPrice!
                    : prev.partnerFutureRoomPrice,
            originalEntrantAliveAtPartnerEntry:
                coupleValidation.originalEntrantAliveAtPartnerEntry !== undefined
                    ? coupleValidation.originalEntrantAliveAtPartnerEntry
                    : prev.originalEntrantAliveAtPartnerEntry,
            newHomeValue: coupleValidation.downsizeNewHomeValue ?? prev.newHomeValue,
            annualRentCost: coupleValidation.partnerAnnualRent ?? prev.annualRentCost,
            sellingCosts: coupleValidation.sellingCosts ?? prev.sellingCosts,
            emergencyFund: coupleValidation.emergencyFund ?? prev.emergencyFund,
        }));
    }, [coupleValidation]);

    // Keep the "analysing" UI on screen for at least the opening's floor, even when the simulation
    // finishes quickly. We really are running hundreds of scenarios, and showing that work is part
    // of the experience — without a floor it can flash past in a fraction of a second. A re-opened
    // saved plan has no floor (engineOpening.ts), and once any run completes the panel is back on
    // the thinking opening.
    const stopLoadingAfterMinDelay = (startedAt: number) => {
        const remaining = remainingFloorMs(openingRef.current, startedAt, Date.now());
        setTimeout(() => {
            setLoading(false);
            openingRef.current = 'thinking';
        }, remaining);
    };

    /**
     * The just-in-time "how would new money be invested?" gate. The aged care engine explores
     * downsize scenarios (booking downsizer contributions into super / a pension) whenever a
     * target home value is in play — which the default settings provide — so the question is
     * asked BEFORE the engine prices anything, same rules as the village module: answering saves
     * to the profile (asked once, ever, across all modules); skipping runs at Conservative
     * without recording an answer.
     *
     * It is only reached by flows that never open the Strategy Analysis Setup modal (a single
     * client with no structures), because that modal now carries the same question among the
     * run's other inputs. Completing the setup counts as having been asked: its default option,
     * "Not sure — assume Conservative", is a legitimate answer that deliberately records nothing,
     * so testing `hasAnsweredNewMoneyProfile` alone put the interim screen back in front of every
     * adviser who left the dropdown alone — the exact screen moving the question was meant to
     * remove.
     */
    const [newMoneySkipped, setNewMoneySkipped] = useState(false);
    /*
     * THE ANNUITY WILLINGNESS QUESTION (owner, 25 Sep 2026). The Strategy Analysis Setup asks it,
     * but that pop-over only opens for some flows — a single client with no structures never sees
     * it — so the gate stands in, exactly as the new-money question does, and suppresses itself
     * once an answer is recorded anywhere.
     */
    const [annuityPrefSkipped, setAnnuityPrefSkipped] = useState(false);
    const acNeedsAnnuityAnswer = !annuityPreferenceOf(factFind as never).answered && !annuityPrefSkipped;
    const answerAnnuityPreference = (appetite: AnnuityAppetite, residual: AnnuityResidual) => {
        if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), annuityAppetite: appetite, annuityResidual: residual } }));
        else setAnnuityPrefSkipped(true);
    };
    const acRunWouldInvest = (Number(coupleValidation?.downsizeNewHomeValue) || 0) > 0;
    const acNewMoneyAskedInSetup = !!coupleValidation?.suggestionEngineSetupComplete;
    const acNeedsNewMoneyAnswer =
        acRunWouldInvest && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped && !acNewMoneyAskedInSetup;
    const answerNewMoneyProfile = (id: InvestmentProfileId) => {
        if (setFactFind) {
            setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
        } else {
            setNewMoneySkipped(true);
        }
    };

    // The Transfer Balance Cap question, chained after the new-money one: the downsize scenarios
    // can commence a pension from the downsizer contribution, so when a person's balances trip the
    // $1.6m screen the ATO figure is asked for before pricing (transferBalanceCap.ts). Skipping
    // estimates from current pension balances; the engine splits at the cap either way.
    const [tbcSkipped, setTbcSkipped] = useState(false);
    const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && acRunWouldInvest
        ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
            : (factFind?.adviceFor === 'couple' && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
        : null;
    const acNeedsTbcAnswer = !acNeedsNewMoneyAnswer && tbcPersonNeedingAnswer !== null;
    const answerTbc = (amount: number) => {
        if (setFactFind && tbcPersonNeedingAnswer) {
            const key = tbcPersonNeedingAnswer;
            setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
        } else {
            setTbcSkipped(true);
        }
    };

    // Run analysis when data changes
    useEffect(() => {
        if (acNeedsNewMoneyAnswer || acNeedsTbcAnswer) { setLoading(false); return; }
        if (factFind && agedCareCosts) {
            setLoading(true);
            const analysisStartedAt = Date.now();
            const runAnalysis = async () => {
                try {
                    // Yield one tick so React can paint the loader BEFORE the analysis runs.
                    // The simulation is synchronous and blocks the main thread (~1s+), so without
                    // this the previous screen freezes and the loader only flashes at the very end.
                    await new Promise((resolve) => setTimeout(resolve, 50));
                    // Debug: Log what agedCareCosts contains
                    
                    // Extract adjusted living expenses from agedCareCosts.
                    // Future-life-event inputs must match Strategy Analysis Setup (coupleValidation): the gear modal
                    // merges these in runAnalysisWithSettings, but the initial/re-run effect previously did not,
                    // so future-aware scoring and disclosure never activated until a settings save.
                    const defaultPartnerRoom = parseFloat(String(agedCareCosts?.radAmount)) || 500000;
                    const adjustedGoals = {
                        ...goals,
                        adjustedLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses),
                        enablePartnerFutureEvent:
                            coupleValidation?.enablePartnerFutureEvent !== undefined
                                ? coupleValidation.enablePartnerFutureEvent
                                : (goals.enablePartnerFutureEvent ?? (factFind?.adviceFor === 'couple')),
                        partnerEntryYear: coupleValidation?.partnerEntryYear ?? goals.partnerEntryYear ?? 3,
                        partnerFutureRoomPrice:
                            (coupleValidation?.partnerFutureRoomPrice || 0) > 0
                                ? coupleValidation!.partnerFutureRoomPrice!
                                : ((Number(goals.partnerFutureRoomPrice) || 0) > 0 ? goals.partnerFutureRoomPrice : defaultPartnerRoom),
                        originalEntrantAliveAtPartnerEntry:
                            coupleValidation?.originalEntrantAliveAtPartnerEntry !== undefined
                                ? coupleValidation.originalEntrantAliveAtPartnerEntry
                                : (goals.originalEntrantAliveAtPartnerEntry ?? true),
                    };
                    
                    // INTEGRATED APPROACH: Generate scenarios and call manual control directly with progress tracking
                    // Downsize is included when parent passes exploreAlternatives/downsizeNewHomeValue, or after user sets it in settings and clicks Save & Re-optimise
                    const results = await generateScenariosAndCallManualControl(
                        factFind,
                        agedCareCosts,
                        adjustedGoals,
                        legacyYears,
                        coupleValidation,
                        setProgress, // Pass setProgress for progress tracking
                        // The entry date the adviser set on the Analyser, so the ranked candidates are
                        // projected against the same case the manual strategy is.
                        careStartDateFromStrategyOptions(strategyOptions),
                        acAnnuityQuote,
                        radDrawdownFromStrategyOptions(strategyOptions),
                        feeArrangementsFromStrategyOptions(strategyOptions),
                        secondRoomFromStrategyOptions(strategyOptions),
                        facilityMoveFromStrategyOptions(strategyOptions),
                        villageExitFromStrategyOptions(strategyOptions),
                    );
                    
                    setAnalysis(results);
                    setError(null);
                } catch (err) {
                    console.error('Ã°Å¸â€Â Error in analyzeFundingStrategies:', err);
                    setError(err instanceof Error ? err.message : String(err));
                } finally {
                    stopLoadingAfterMinDelay(analysisStartedAt);
                }
            };

            runAnalysis();
        }
    }, [factFind, agedCareCosts, goals, legacyYears, coupleValidation, acNeedsNewMoneyAnswer, acNeedsTbcAnswer, acAnnuityQuote]);

    const saveSelectedStrategy = (strategy: Strategy) => {

        // The row's own costs (after the deposit), both rooms where it carries a second one; the
        // base costs where the row has none.
        const annualAgedCareCost = strategyHouseholdDailyCost(strategy as never, agedCareCosts as never) * 365;
        
        // Audit trail for SOA: what was recommended vs what the client selected
        const recommendedList = analysis?.recommendedOptimalStrategies || [];
        const recommendedOptimalStrategies = recommendedList.map(ro => ({ label: ro.label, name: ro.strategy?.name }));
        const clientSelectedIsAlternative = recommendedList.length > 0 && !recommendedList.some(ro => ro.strategy?.name === strategy.name);
        
        const savedStrategy = {
            id: `strategy-${Date.now()}`,
            name: strategy.name,
            type: 'aged_care',
            source: 'suggestion_engine',
            strategy: strategy,
            createdAt: new Date().toISOString(),
            recommendedOptimalStrategies,
            clientSelectedIsAlternative,
            outputs: {
                agedCareCost: annualAgedCareCost, // Use strategy-specific cost after RAD payment
                agePension: strategy.snapshot?.pension || strategy.agePension || strategy.estimatedAgePension || 0,
                cashFlow: strategy.snapshot?.netCashflow ?? strategy.snapshot?.cashFlow ?? strategy.cashFlow ?? strategy.cashflow ?? 0,
                legacyEstimate: strategy.isFutureEventOverlayCard && strategy.futureAdjustedLegacy != null
                    ? strategy.futureAdjustedLegacy
                    : (strategy.snapshot?.legacyValue || strategy.legacyValue || strategy.projectedEstateValue || 0),
                roomCost: agedCareCosts.radAmount || 0, // Add room cost for identification
                livingExpenses: strategy.snapshot?.livingExpenses ?? scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) // Save adjusted living expenses
            },
            inputs: {
                scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
            }
        };


        // Save to session storage
        const existingStrategies = JSON.parse(sessionStorage.getItem('careCompanionStrategies') || '[]');
        const updatedStrategies = [...existingStrategies, savedStrategy];
        sessionStorage.setItem('careCompanionStrategies', JSON.stringify(updatedStrategies));
        setActiveHomeCareStrategyId(formatServiceBuilderSelectionId('aged_care', savedStrategy.id));
        notifyHomeCareStrategiesUpdated();

        // Notify parent component
        if (onStrategySelect) {
            onStrategySelect(savedStrategy);
        } else {
        }
    };

    const handleDeepDive = (strategy: Strategy) => {
        setSelectedScenario(strategy);
        setDeepDiveModal(true);
    };

    const handleSettingsClick = () => {
        if (analysis && analysis.strategies && analysis.strategies.length > 0) {
            setShowSaveWarning(true);
        } else {
            setShowSettingsModal(true);
        }
    };

    const handleSaveWarningResponse = (action: 'save' | 'continue' | 'cancel') => {
        setShowSaveWarning(false);
        if (action === 'save') {
            // Save current recommended strategy
            if (analysis && analysis.strategies && analysis.strategies.length > 0) {
                const recommendedStrategy = analysis.strategies[0]!;
                saveSelectedStrategy(recommendedStrategy);
            }
        }
        if (action === 'continue' || action === 'save') {
            setShowSettingsModal(true);
        }
    };

    const handleSettingsSave = (newSettings: SuggestionEngineSettings) => {
        const { structureFundingInSuggestions: nextStructureFunding, ...engineSettings } = newSettings;
        if (setFactFind && nextStructureFunding != null) {
            setFactFind((prev) => ({ ...prev, structureFundingInSuggestions: nextStructureFunding }));
        }
        setCurrentSettings(engineSettings);
        setShowSettingsModal(false);

        setAnalysis(null);
        setLoading(true);

        setTimeout(() => {
            runAnalysisWithSettings(
                engineSettings,
                nextStructureFunding != null ? { structureFundingInSuggestions: nextStructureFunding } : {}
            );
        }, 100);
    };

    /**
     * Change how many years the estate figure looks ahead, from the results themselves.
     *
     * The control also lives in the settings form, which is where it used to live ONLY. A client
     * looking at "Projected Estate Value" on 2026-08-21 said "but I don't know how long my dad will
     * live", could not remember the figure was a 5-year projection, and wanted to try other periods
     * without hunting for the panel that set it. Same code path as saving the settings form, so the
     * two cannot produce different runs.
     */
    const handleHorizonChange = (years: number) => {
        const next = { ...currentSettings, legacyYears: years };
        setCurrentSettings(next);
        setAnalysis(null);
        setLoading(true);
        setTimeout(() => runAnalysisWithSettings(next), 100);
    };

    const runAnalysisWithSettings = async (settings: SuggestionEngineSettings, formDataOverrides: Record<string, unknown> = {}) => {
        const settingsAnalysisStartedAt = Date.now();
        try {
            // Yield so the loader paints before the synchronous, main-thread-blocking analysis.
            await new Promise((resolve) => setTimeout(resolve, 50));
            const updatedFactFind: DraftFactFind = {
                ...factFind,
                ...formDataOverrides,
                emergencyFund: settings.emergencyFund,
            };

            // Create updated goals that include the simplified settings
            const adjustedGoals = {
                ...goals,
                adjustedLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, updatedFactFind.expenses?.livingExpenses),
                emergencyFund: settings.emergencyFund,
                // Alternative scenario parameters
                newHomeValue: settings.newHomeValue,
                annualRentCost: settings.annualRentCost,
                // Partner living arrangement
                partnerLivingArrangement: settings.partnerLivingArrangement,
                // Future event assumptions
                enablePartnerFutureEvent: settings.enablePartnerFutureEvent,
                partnerEntryYear: settings.partnerEntryYear,
                partnerFutureRoomPrice: settings.partnerFutureRoomPrice,
                originalEntrantAliveAtPartnerEntry: settings.originalEntrantAliveAtPartnerEntry,
            };
            
            // Include downsize when "New home value (if downsizing)" is set; pass selling costs $ for downsize.
            // Downsize is now driven purely off downsizeNewHomeValue (see downsizeEnabled), not a magic string.
            const effectiveCoupleValidation: CoupleValidation = (Number(settings.newHomeValue) > 0)
                ? { ...coupleValidation, downsizeNewHomeValue: Number(settings.newHomeValue), sellingCosts: parseFloat(String(settings.sellingCosts)) || 0, stampDuty: Number(settings.stampDuty) || 0, newHomeState: settings.newHomeState }
                : { ...coupleValidation, sellingCosts: parseFloat(String(settings.sellingCosts)) || 0 };
            const result = await generateScenariosAndCallManualControl(updatedFactFind, agedCareCosts, adjustedGoals, settings.legacyYears, effectiveCoupleValidation, null, careStartDateFromStrategyOptions(strategyOptions), null, radDrawdownFromStrategyOptions(strategyOptions), feeArrangementsFromStrategyOptions(strategyOptions), secondRoomFromStrategyOptions(strategyOptions), facilityMoveFromStrategyOptions(strategyOptions), villageExitFromStrategyOptions(strategyOptions));
            setAnalysis(result);
        } catch (error) {
            console.error('Error re-running analysis:', error);
            setError('Failed to re-optimise with new settings');
        } finally {
            stopLoadingAfterMinDelay(settingsAnalysisStartedAt);
        }
    };

    // Keep this memo before early-return branches below to preserve hook order.
    // Primary recommendations use baseline legacy; optional fourth card shows the best strategy
    // under future-event-adjusted legacy when partner-later assumptions are enabled.
    const futureEventOptimalStrategy = useMemo<Strategy | null>(() => {
        if (!currentSettings.enablePartnerFutureEvent) return null;
        if (!analysis?.strategies?.length) return null;

        const partnerEntryYear = parseInt(String(currentSettings.partnerEntryYear), 10) || 3;
        const partnerFutureRoomPrice = parseFloat(String(currentSettings.partnerFutureRoomPrice)) || 0;
        const originalEntrantAliveAtPartnerEntry = !!currentSettings.originalEntrantAliveAtPartnerEntry;
        if (partnerFutureRoomPrice <= 0) return null;

        const horizonYears = currentSettings.legacyYears || legacyYears || 5;
        const scored = analysis.strategies
            .map((strategy) => {
                const result = computeFutureEventAdjustedLegacy({
                    strategy: strategy as unknown as EvaluatedStrategy,
                    legacyYears: horizonYears,
                    partnerEntryYear,
                    partnerRoomPrice: partnerFutureRoomPrice,
                    originalEntrantAliveAtEvent: originalEntrantAliveAtPartnerEntry,
                    baseAgedCareCosts: (strategy.strategySpecificAgedCareCosts as AgedCareCosts) || agedCareCosts,
                });
                if (!result) return null;
                return {
                    strategy,
                    result,
                    adjustedLegacy: result.adjustedLegacy,
                    radPaid: estimateRadPaidForStrategy(strategy as unknown as EvaluatedStrategy),
                };
            })
            .filter((row): row is NonNullable<typeof row> => row !== null);

        if (scored.length === 0) return null;

        // Primary: highest future-adjusted legacy. Tie-break (within band): lowest estimated RAD paid.
        const maxAdjustedLegacy = scored.reduce((m, row) => Math.max(m, row.adjustedLegacy), -Infinity);
        if (!Number.isFinite(maxAdjustedLegacy)) return null;

        const bandUsd = SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD;
        const threshold = maxAdjustedLegacy - bandUsd;
        const nearBest = scored.filter((row) => row.adjustedLegacy >= threshold);

        const topAdjustedRow = scored.reduce((best, row) => (row.adjustedLegacy > best.adjustedLegacy ? row : best), scored[0]!);
        const pickedRow = nearBest.reduce((best, row) => {
            if (row.radPaid < best.radPaid) return row;
            if (row.radPaid === best.radPaid && row.adjustedLegacy > best.adjustedLegacy) return row;
            return best;
        }, nearBest[0]!);

        const usedLowerRadTieBreak = pickedRow.strategy?.name !== topAdjustedRow.strategy?.name;

        return {
            ...pickedRow.strategy,
            isFutureEventOverlayCard: true,
            name: `${pickedRow.strategy.name} (Partner enters later)`,
            recommendationLabels: [
                usedLowerRadTieBreak
                    ? 'Optimal Legacy (Partner Later) — lower RAD option'
                    : 'Optimal Legacy (Partner Later Scenario)',
            ],
            futureEventAssumptions: pickedRow.result.assumptions,
            futureAdjustedLegacy: pickedRow.adjustedLegacy,
            futureEventAdjustmentDelta: pickedRow.result.adjustmentDelta,
            futureEventMeta: {
                bandUsd,
                baselineLegacy: pickedRow.result.baselineLegacy,
                maxAdjustedLegacy,
                selectedAdjustedLegacy: pickedRow.adjustedLegacy,
                topAdjustedStrategyName: topAdjustedRow.strategy?.name,
                selectedStrategyName: pickedRow.strategy?.name,
                radPaidSelected: pickedRow.radPaid,
                radPaidTopAdjusted: topAdjustedRow.radPaid,
                usedLowerRadTieBreak,
            },
            // Keep baseline snapshot legacyValue intact; the future-aware value is carried separately.
        };
    }, [analysis, agedCareCosts, currentSettings, legacyYears]);

    const showFutureEventFourthCard = !!futureEventOptimalStrategy;

    const lowerRadNearBestStrategy = useMemo<Strategy | null>(() => {
        if (!analysis?.strategies?.length) return null;
        /*
         * THE SAME FIELD AS THE LEADERS (owner-caught, 9 Oct 2026). A strategy the horizon rule set
         * aside — an annuity that needs more time than the client's age and health support — can
         * never be crowned; it must not define the near-best band or fill this slot either. It did:
         * the $200k annuity's estate set the band and the $100k annuity, flagged too, was offered as
         * the "lower RAD" alternative with a higher estate than the card above it.
         */
        const eligible = (analysis.strategies as Strategy[]).filter((s) => engineStrategyHorizonWarning(s, factFind) == null);
        const picked = pickLowerRadNearBestStrategy(
            eligible as unknown as EvaluatedStrategy[],
            SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD
        );
        if (!picked) return null;
        const { strategy, maxLegacy, bandUsd, topLegacyStrategyName } = picked;
        const topRow = analysis.strategies.find((s) => s.name === topLegacyStrategyName);
        return {
            ...(strategy as unknown as Strategy),
            isLowerRadNearBestCard: true,
            recommendationLabels: [LOWER_RAD_NEAR_BEST_LEGACY_BADGE],
            lowerRadNearBestMeta: {
                maxLegacy,
                bandUsd,
                topLegacyStrategyName,
                radPaidThis: estimateRadPaidForStrategy(strategy),
                radPaidTop: estimateRadPaidForStrategy((topRow || {}) as unknown as EvaluatedStrategy),
            },
        };
    }, [analysis, factFind]);

    const showLowerRadNearBestCard = !!lowerRadNearBestStrategy;
    if (acNeedsNewMoneyAnswer) {
        return (
            <div className="max-w-3xl mx-auto mt-6">
                <NewMoneyProfileGate
                    onAnswer={answerNewMoneyProfile}
                    onSkip={() => setNewMoneySkipped(true)}
                    contextLine="Some of the strategies I'll compare invest money that gets freed up — for example if downsizing the home is modelled alongside the aged care move."
                />
            </div>
        );
    }
    if (acNeedsAnnuityAnswer) {
        return (
            <div className="max-w-3xl mx-auto mt-6">
                <AnnuityPreferenceGate
                    offerGuaranteed={guaranteedShapeAvailable(factFind as never)}
                    guaranteeWindow={guaranteeWindowWords({
                        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
                        sex: factFind?.client?.sex,
                        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
                        partnerSex: factFind?.partner?.sex,
                        ownerName: factFind?.client?.firstName,
                        partnerName: factFind?.partner?.firstName,
                    })}
                    onAnswer={answerAnnuityPreference}
                    onSkip={() => setAnnuityPrefSkipped(true)}
                    contextLine="Some of the strategies I'll compare move savings into a guaranteed income to lower the care fees."
                />
            </div>
        );
    }
    if (acNeedsTbcAnswer) {
        return (
            <div className="max-w-3xl mx-auto mt-6">
                <TransferCapGate
                    name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                    onAnswer={answerTbc}
                    onSkip={() => setTbcSkipped(true)}
                />
            </div>
        );
    }
    const waitingForAnalysis = factFind && agedCareCosts && !analysis && !error;
    if ((loading || waitingForAnalysis) && openingRef.current === 'quiet') {
        // A saved plan coming back: no illustration, no floor — just a moment while its figures are rebuilt.
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-50" data-testid="engine-quiet-opening">
                <div className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 shadow-card px-5 py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-200 border-t-blue-600" />
                    <p className="text-sm text-slate-700">Opening your saved plan…</p>
                </div>
            </div>
        );
    }
    if (loading || waitingForAnalysis) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-50">
                <div className="text-center max-w-lg px-6">
                    {/* Analysis illustration: chart bars + trend */}
                    <div className="mb-8 flex justify-center">
                        <svg width="240" height="140" viewBox="0 0 240 140" fill="none" className="text-blue-600/90">
                            <defs>
                                <linearGradient id="barGrad" x1="0" y1="1" x2="0" y2="0">
                                    <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="currentColor" stopOpacity="0.9" />
                                </linearGradient>
                            </defs>
                            <rect x="40" y="70" width="28" height="50" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s' }} />
                            <rect x="80" y="50" width="28" height="70" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.2s' }} />
                            <rect x="120" y="35" width="28" height="85" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.4s' }} />
                            <rect x="160" y="55" width="28" height="65" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.6s' }} />
                            <rect x="200" y="25" width="28" height="95" rx="4" fill="url(#barGrad)" className="animate-pulse" style={{ animationDuration: '1.5s', animationDelay: '0.8s' }} />
                            <path d="M 48 95 Q 88 75 128 60 T 208 45" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.7" strokeDasharray="6 4" className="animate-pulse" style={{ animationDuration: '2s' }} />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">
                        {analysis ? 'Re-optimising with new settings...' : 'Optimising your funding strategies'}
                    </h2>
                    <p className="text-gray-600 mb-6">
                        We're working through a large number of simulated options - this may take a few minutes.
                    </p>
                    {progress.total > 0 && (
                        <p className="text-sm text-blue-700 font-medium">
                            Running scenario {progress.current} of {progress.total}
                        </p>
                    )}
                    <div className="mt-6 flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-200 border-t-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white border border-slate-200/70 border-l-4 border-red-400 rounded-lg p-6">
                <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
                        <div>
                        <h3 className="text-lg font-medium text-red-800">Analysis Error</h3>
                        <p className="text-red-600 mt-1">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!analysis || !analysis.strategies || analysis.strategies.length === 0) {
        return (
            <div className="text-center p-8">
                <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Strategies Available</h3>
                <p className="text-gray-600">Please check your input data and try again.</p>
            </div>
        );
    }

    // Top 3 recommended strategies (Optimal Legacy, Optimal Cashflow, Optimal Age Pension); fallback to first 3 if no recommended list
    const recommended = analysis.recommendedOptimalStrategies || [];
    const topStrategies = (() => {
        if (recommended.length === 0) return analysis.strategies.slice(0, 3);

        // Collapse duplicate strategies and keep all recommendation labels as badges.
        const byStrategyName = new Map<string, Strategy>();
        recommended.forEach((row) => {
            const name = row?.strategy?.name;
            if (!name || !row?.strategy) return;

            if (!byStrategyName.has(name)) {
                byStrategyName.set(name, { ...row.strategy, recommendationLabels: [row.label] });
                return;
            }

            const existing = byStrategyName.get(name)!;
            const labels = existing.recommendationLabels || [];
            if (row.label && !labels.includes(row.label)) labels.push(row.label);
            byStrategyName.set(name, { ...existing, recommendationLabels: labels });
        });

        const uniqueRecommended = Array.from(byStrategyName.values());
        if (uniqueRecommended.length >= 3) return uniqueRecommended.slice(0, 3);

        const used = new Set(uniqueRecommended.map((s) => s.name));
        const fillers = analysis.strategies.filter((s) => !used.has(s.name)).slice(0, 3 - uniqueRecommended.length);
        return [...uniqueRecommended, ...fillers];
    })();
    // Goal-first display (v1): legacy + "all goals" surface = Optimal Legacy + optional Lower RAD + optional future-event card;
    // cashflow / pension = single primary card only (no Lower RAD / future-event extras — keeps non-legacy views light).
    const effectiveOptimisationSurface = optimisationFocus === 'all' ? 'legacy' : optimisationFocus;
    const displayedGoalStrategies = (() => {
        const rec = analysis.recommendedOptimalStrategies || [];
        const enrich = (label: string): Strategy | null => {
            const row = rec.find((r) => r.label === label);
            if (!row?.strategy?.name) return null;
            const merged = topStrategies.find((s) => s.name === row.strategy.name);
            return merged || row.strategy;
        };
        if (effectiveOptimisationSurface === 'covered') {
            // The same plan as Best estate is said in one line, not shown twice (owner, 24 Sep 2026).
            if (coveredIsBestEstate(rec)) return [];
            const s = enrich(CASHFLOW_COVERED_LABEL);
            return s ? [s] : (topStrategies[0] ? [topStrategies[0]] : []);
        }
        if (effectiveOptimisationSurface === 'cashflow') {
            const s = enrich('Optimal Cashflow');
            return s ? [s] : (topStrategies[0] ? [topStrategies[0]] : []);
        }
        if (effectiveOptimisationSurface === 'pension') {
            const s = enrich('Optimal Age Pension');
            return s ? [s] : (topStrategies[0] ? [topStrategies[0]] : []);
        }
        const out = [];
        const leg = enrich('Optimal Legacy');
        if (leg) out.push(leg);
        if (showLowerRadNearBestCard && lowerRadNearBestStrategy) {
            out.push(lowerRadNearBestStrategy);
        }
        if (showFutureEventFourthCard && futureEventOptimalStrategy) {
            out.push(futureEventOptimalStrategy);
        }
        if (out.length) return out;
        return [
            ...topStrategies,
            ...(showFutureEventFourthCard ? [futureEventOptimalStrategy] : []),
            ...(showLowerRadNearBestCard ? [lowerRadNearBestStrategy] : []),
        ].filter(Boolean);
    })();
    const innerSelectedStrategyForHighlight = selectedStrategy?.strategy ?? selectedStrategy;
    let suggestionEngineCardHighlightKey =
        innerSelectedStrategyForHighlight?.name != null
            ? strategyCardSelectionKey(innerSelectedStrategyForHighlight)
            : null;
    if (
        suggestionEngineCardHighlightKey &&
        !displayedGoalStrategies.some((s) => strategyCardSelectionKey(s) === suggestionEngineCardHighlightKey)
    ) {
        suggestionEngineCardHighlightKey = null;
    }
    // Rows for "Compare all strategies": goal-type leaders + optional cards only (not all simulated scenarios).
    const goalComparisonRows = (() => {
        const rec = analysis.recommendedOptimalStrategies || [];
        const enrich = (label: string): Strategy | null => {
            const row = rec.find((r) => r.label === label);
            if (!row?.strategy?.name) return null;
            const merged = topStrategies.find((s) => s.name === row.strategy.name);
            return merged || row.strategy;
        };
        const rows = [];
        const cov = enrich(CASHFLOW_COVERED_LABEL);
        if (cov) rows.push({ goalLabel: CASHFLOW_COVERED_CHIP, strategy: cov });
        const leg = enrich('Optimal Legacy');
        if (leg) rows.push({ goalLabel: BEST_ESTATE_CHIP, strategy: leg });
        const cf = enrich('Optimal Cashflow');
        if (cf) rows.push({ goalLabel: 'Best cashflow', strategy: cf });
        const pen = enrich('Optimal Age Pension');
        if (pen) rows.push({ goalLabel: 'Best pension', strategy: pen });
        if (showLowerRadNearBestCard && lowerRadNearBestStrategy) {
            rows.push({ goalLabel: LOWER_RAD_NEAR_BEST_LEGACY_BADGE, strategy: lowerRadNearBestStrategy });
        }
        if (showFutureEventFourthCard && futureEventOptimalStrategy) {
            rows.push({ goalLabel: 'Partner enters later (scenario)', strategy: futureEventOptimalStrategy });
        }
        return rows;
    })();
    const legacyRankByName = (() => {
        const ranked = new Map();
        const sortedByLegacy = [...(analysis?.strategies || [])]
            .sort((a, b) => (b.snapshot?.legacyValue ?? -Infinity) - (a.snapshot?.legacyValue ?? -Infinity));
        let rank = 1;
        sortedByLegacy.forEach((s) => {
            if (!s?.name || ranked.has(s.name)) return;
            ranked.set(s.name, rank);
            rank += 1;
        });
        return ranked;
    })();
    const tableStrategies = (() => {
        const base = analysis?.allSimulatedStrategies || analysis?.strategies || [];
        const sorted = [...base];
        if (allStrategiesSortBy === 'cashflow') {
            sorted.sort((a, b) => (b.snapshot?.netCashflow ?? -Infinity) - (a.snapshot?.netCashflow ?? -Infinity));
        } else if (allStrategiesSortBy === 'pension') {
            sorted.sort((a, b) => (b.snapshot?.pension ?? -Infinity) - (a.snapshot?.pension ?? -Infinity));
        } else {
            sorted.sort((a, b) => (b.snapshot?.legacyValue ?? -Infinity) - (a.snapshot?.legacyValue ?? -Infinity));
        }
        return sorted;
    })();

    // Show SOA chooser view if strategy is selected
    if (showSOAView && selectedStrategyForSOA) {
        return (
            <div className="space-y-6">
                {/* SOA Header */}
                <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                                <h2 className="text-2xl font-bold">Selected Strategy: {selectedStrategyForSOA.name}</h2>
                                <button
                                    onClick={() => setShowSOAView(false)}
                                    className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-all duration-200 backdrop-blur-sm border border-white/30 ml-4"
                                >
                                    Back
                                </button>
                            </div>
                            <p className="text-green-100 text-sm">
                                You have selected this strategy for Statement of Advice generation. Choose your preferred output format.
                            </p>
                        </div>
                    </div>
                </div>

                {/* SOA Confirmation Modal */}
                <div className="bg-white border-2 border-green-500 rounded-lg p-8 shadow-lg text-center">
                    <div className="mb-6">
                        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Generate Statement of Advice</h3>
                        <p className="text-gray-600 text-lg">
                            You have selected <span className="font-semibold text-green-600">"{selectedStrategyForSOA.name}"</span> for your Statement of Advice.
                        </p>
                        <p className="text-gray-500 text-sm mt-2">
                            Digital SOA is the new professional read-only format.
                        </p>
                    </div>
                    
                    <div className="flex justify-center gap-3 flex-wrap">
                        <button
                            onClick={() => setShowSOAView(false)}
                            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-all duration-200"
                        >
                            Maybe Later
                        </button>
                        <button
                            onClick={async () => {
                                // buildAgedCareDigitalSOASnapshot's own param interface isn't exported (it's an
                                // internal detail of that module) — cast via its own inferred parameter type
                                // rather than duplicating/exporting an interface just for this call site.
                                const snapshot = await buildAgedCareDigitalSOASnapshot({
                                    selectedStrategy: selectedStrategyForSOA,
                                    factFind,
                                    agedCareCosts,
                                    analysis,
                                    coupleValidation,
                                    reportAudience: 'direct_client'
                                } as unknown as Parameters<typeof buildAgedCareDigitalSOASnapshot>[0]);
                                openAgedCareDigitalSOAAttachment(snapshot);
                                const savedSoaScenario = {
                                    id: `soa-${Date.now()}`,
                                    name: `SE | ${getDisplayStrategyName(selectedStrategyForSOA.name, selectedStrategyForSOA.radPaidNet ?? null, (Number(selectedStrategyForSOA.radRoomPrice) || null), secondRoomOf(selectedStrategyForSOA), moveOf(selectedStrategyForSOA), villageOf(selectedStrategyForSOA))}`,
                                    createdAt: new Date().toISOString(),
                                    soaSource: 'SE',
                                    soaDeliveryStatus: 'draft',
                                    isCurrentForCase: true,
                                    outputs: {
                                        agedCareCost: strategyHouseholdDailyCost(selectedStrategyForSOA as never, agedCareCosts as never) * 365,
                                        agePension: selectedStrategyForSOA.snapshot?.pension || 0,
                                        cashFlow: selectedStrategyForSOA.snapshot?.netCashflow || 0,
                                        legacyEstimate: selectedStrategyForSOA.isFutureEventOverlayCard && selectedStrategyForSOA.futureAdjustedLegacy != null
                                            ? selectedStrategyForSOA.futureAdjustedLegacy
                                            : (selectedStrategyForSOA.snapshot?.legacyValue ?? 0),
                                    },
                                    soaSnapshot: snapshot,
                                };
                                onScenarioSaved?.(savedSoaScenario);
                                setShowSOAView(false);
                            }}
                            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-semibold shadow-md hover:shadow-lg"
                        >
                            Open Digital SOA
                        </button>
                        {/* Legacy PDF option removed: Digital SOA is the single source of truth */}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/*
              * "Understanding your plan" for a SUGGESTION ENGINE strategy — the same component, the
              * same modal and the same baseline Manual Control uses, driven by this strategy's own
              * figures. The tile expanders on each card explain what a FEE is; this explains what
              * this PLAN does, and why each figure moved.
              *
              * Every value below is taken from the strategy row as the engine built it. The one that
              * needs care is `simulatedFinancials`: the recap nets care off itself, so this must be
              * the GROSS financials (strategyFinancials), never `snapshot`, whose cashflow already
              * has care removed. Passing the snapshot would charge the fees twice.
              */}
            {explainStrategy && (
                <PlanExplanationModal
                    strategyName={getDisplayStrategyName(
                        explainStrategy.name,
                        explainStrategy.radPaidNet,
                        explainStrategy.radRoomPrice,
                        secondRoomOf(explainStrategy),
                        moveOf(explainStrategy),
                        villageOf(explainStrategy),
                    )}
                    onClose={() => setExplainStrategy(null)}
                    footer={
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                const s = explainStrategy;
                                setExplainStrategy(null);
                                handleDeepDive(s);
                            }}
                        >
                            See the year-by-year figures
                        </Button>
                    }
                >
                    <StrategyPlanRecap
                        eyebrow={null}
                        factFind={explainStrategy.originalFactFind || factFind}
                        overallStrategy={explainStrategy.strategyOptions || {}}
                        strategyInputs={explainStrategy.strategyOptions || {}}
                        downsizeDetails={explainStrategy.strategyOptions?.downsizeDetails || {}}
                        investmentPropertyDetails={explainStrategy.strategyOptions?.investmentPropertyDetails || {}}
                        reverseMortgageDetails={explainStrategy.strategyOptions?.reverseMortgageDetails || null}
                        heasDetails={explainStrategy.strategyOptions?.heasDetails || null}
                        otherStrategies={explainStrategy.strategyOptions?.otherStrategies || null}
                        // Money the plan already has coming — a village's exit payment, a current room's
                        // refund — opens the story, as it does in the advice document (9 Oct 2026).
                        {...engineRowPieces(explainStrategy, (explainStrategy.originalFactFind || factFind) as DraftFactFind)}
                        annuityRecord={explainStrategy.strategyOptions?.annuityRecord || null}
                        debtPayoffPlan={explainStrategy.strategyOptions?.debtPayoff || null}
                        // The flattened `type-id` keys, not the engine's nested shape — the recap
                        // reads the Manual Control form. Converted server-side by the engine's own
                        // helper so both surfaces name the same funding assets.
                        radFunding={explainStrategy.radFundingManualKeys || {}}
                        cgtOverrides={explainStrategy.strategyOptions?.cgtOverrides || {}}
                        radInput={Number(explainStrategy.radRoomPrice) || 0}
                        radPaid={Number(explainStrategy.radPaidNet) || 0}
                        secondRoom={(() => { const sr = (explainStrategy as { secondRoom?: { person: string; roomPrice: number; radPaid: number; agedCareCosts?: { totalDailyCost?: number } } }).secondRoom; return sr ? { name: roleNames(factFind, sr.person as 'client' | 'partner').secondName, leadName: roleNames(factFind, sr.person as 'client' | 'partner').firstName, roomPrice: sr.roomPrice, radPaid: sr.radPaid, dailyTotal: sr.agedCareCosts?.totalDailyCost ?? null, funding: describeFundingKeys(factFind, (sr as { radFunding?: Record<string, number> }).radFunding), paidFromLabel: (sr as { entersLater?: boolean; careStartDate?: string }).entersLater && (sr as { careStartDate?: string }).careStartDate ? phaseFromLabel((sr as { careStartDate?: string }).careStartDate!) : undefined } : null; })()}
                        agedCareCosts={explainStrategy.strategySpecificAgedCareCosts || {}}
                        simulatedFinancials={explainStrategy.strategyFinancials || {}}
                        extraServiceFeeInput={0}
                        // ONE baseline for the cards and the explanation — passed down, never rebuilt.
                        sharedBaseline={
                            agedCareBaseline?.financials
                                ? { pension: agedCareBaseline.pension, cashFlowAfterCare: agedCareBaseline.cashFlowAfterCare }
                                : null
                        }
                        planRunway={runwayFactsOf(explainStrategy.projections)}
                        moneyOrder={(() => {
                            // The selected card reads the rail's long-run rows; any other card, its own plan-period rows.
                            const isSelected = !!selectedStrategy && (selectedStrategy as { id?: string }).id != null && (selectedStrategy as { id?: string }).id === (explainStrategy as { id?: string }).id;
                            const rows = (isSelected && selectedPlanRunwayRows?.length ? selectedPlanRunwayRows : (explainStrategy.projections ?? [])) as RunwayRow[];
                            const runway = planRunwayCopy(rows as never, rows.length);
                            const ff = (explainStrategy.originalFactFind || factFind) as DraftFactFind;
                            const forWhom = (ff.goals as { aged_care_for?: string } | undefined)?.aged_care_for;
                            const refundTo = ff.adviceFor === 'couple' && forWhom !== 'both'
                                ? ((forWhom === 'partner' ? ff.client?.firstName : ff.partner?.firstName) || 'your partner')
                                : 'the estate';
                            return moneyOrderParagraph(rows, ff, runway, { refund: Number(explainStrategy.radPaidNet) || 0, refundTo });
                        })()}
                        baselineLegacy={agedCareBaseline?.legacyValue ?? null}
                        planLegacy={
                            Array.isArray(explainStrategy.projections) && explainStrategy.projections.length > 0
                                ? explainStrategy.projections[explainStrategy.projections.length - 1]?.netWorth ?? null
                                : null
                        }
                        legacyYears={legacyYears}
                        /*
                         * The engine's annuity terms, told INSIDE the story rather than bolted on
                         * after it. Rendered alongside the recap, the modal showed one small box and
                         * nothing else whenever the recap had nothing to say (owner, 2026-09-01).
                         */
                        engineAnnuityTerms={(explainStrategy as { annuityTerms?: Record<string, unknown> }).annuityTerms}
                        equityRelease={(explainStrategy as { equityRelease?: Record<string, unknown> }).equityRelease}
                        /*
                         * ENTER THE REAL QUOTE (owner, 2026-09-01: the Suggestion Engine "doesn't
                         * provide the ability to enter the actual quote"). Committing one re-runs
                         * the whole analysis, because a real rate can change which strategy wins -
                         * relabelling this one card would leave the ranking built on our rate.
                         */
                        annuityQuoteControl={<AnnuityQuoteControl
                            draft={acAnnuityQuoteDraft}
                            onDraft={setAcAnnuityQuoteDraft}
                            committed={acAnnuityQuote}
                            onCommit={(next) => { setAcAnnuityQuote(next); setExplainStrategy(null); }}
                        />}
                    />
                </PlanExplanationModal>
            )}
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center justify-between gap-6">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-2xl font-bold mb-2">Suggestion Engine</h2>
                        <p className="text-blue-100 text-sm leading-relaxed">
                            <span className="font-semibold text-white">{analysis.totalScenarios || analysis.strategies.length} strategy combinations</span>
                            {' '}analysed. Outcomes are{' '}
                            <span className="font-semibold text-white">projections, not guarantees</span>.
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setShowAssumptionsModal(true)}
                            className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border border-white/30 text-sm"
                        >
                            Assumptions
                        </button>
                        <button
                            onClick={handleSettingsClick}
                            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 backdrop-blur-sm border border-white/30"
                            title="Edit Settings"
                        >
                            <Settings className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>

            {(factFind?.structures || []).length > 0 && (
                <div className="p-4 rounded-lg border border-slate-200/70 border-l-4 border-amber-400 bg-white text-sm text-amber-950 space-y-1">
                    <p className="font-semibold">Trusts and companies in strategy suggestions</p>
                    <p>
                        {isStructureFundingIncludedInSuggestions(factFind)
                            ? 'Automatic suggestions may use trust or company liquid assets and whole property sales. Pension and care fee estimates always include your structures.'
                            : 'Automatic suggestions use personal assets only. Trust and company assets still count toward pension and care fee estimates.'}
                    </p>
                    <p className="text-xs">
                        Change this in Strategy Analysis Setup (when entering the Suggestion Engine) or via the settings
                        gear above.
                    </p>
                </div>
            )}

            {/* Assumptions modal */}
            {showAssumptionsModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAssumptionsModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">Assumptions & limitations</h3>
                            <button type="button" onClick={() => setShowAssumptionsModal(false)} className="text-gray-500 hover:text-gray-700 p-1">×</button>
                        </div>
                        <div className="p-4 overflow-auto text-sm text-gray-700 space-y-4">
                            <p className="text-gray-600">The Suggestion Engine uses the following assumptions. For scenarios that fall outside these, use Manual Control.</p>
                            <ul className="list-disc list-inside space-y-2">
                                <li><strong>Funding optimisation:</strong> For each scenario (e.g. "Pay 50% RAD, Keep Home"), we test every possible order of drawing down from your liquid assets (bank, account-based pensions, shares). We then select the outcome that gives the best projected estate value (legacy) for that scenario. So the strategy shown is the optimal funding mix we found, not a single fixed path.</li>
                                <li><strong>Downsize residual:</strong> When modelling a downsize, surplus from the sale (after the new home purchase) is allocated to the RAD and/or the bank by default. Eligible clients (55+) may also model max downsizer contributions to super, or commencing an account-based pension at minimum drawdown where age-eligible (60+). Other growth assets (e.g. shares, investment properties) are not used for the residual because in aged care we face longevity risk and volatility. If you want to model allocating the residual to shares or property, use the Manual Control section.</li>
                                <li><strong>RAD levels:</strong> Strategies are tested across a range of RAD levels (e.g. 10% to 100% of the room price). Each level shows the outcome if that amount is paid as a Refundable Accommodation Deposit.</li>
                                <li><strong>Selling costs & new home value:</strong> Downsize scenarios use the selling costs and new home value you entered in settings (or in Manual Control). Net proceeds = sale price - selling costs; surplus = net proceeds - new home cost.</li>
                                <li><strong>Emergency fund & legacy years:</strong> Settings such as emergency fund protection and the projection period (legacy years) apply to all strategies. Change these via the settings (gear) icon.</li>
                                <li><strong>Income, growth & cost base:</strong> Projected results factor in the income rates, growth rates and any applicable cost base (e.g. for shares or investment property) entered in the fact find. If these assumptions differ in practice, the optimised result may be impacted.</li>
                                {/*
                                  * THIS SAID THE OPPOSITE OF WHAT THE ENGINE DOES.
                                  *
                                  * It read "Annuity not in Suggestion Engine: The Suggestion Engine
                                  * does not evaluate purchasing a lifetime or fixed-term annuity."
                                  * True when written; false since the annuity levers went in. The
                                  * engine now builds up to eight annuity candidates per funding
                                  * scenario (four sizes x two residual-capital variants) and ranks
                                  * them alongside everything else - so a client could read that
                                  * annuities were ignored while looking at a plan containing one
                                  * (owner-caught, 2026-09-01).
                                  *
                                  * The caution that text carried was sound and is kept, because it
                                  * is the honest thing to say about this cohort: a deposit is the
                                  * stronger lever here, and it is refundable.
                                  */}
                                <li><strong>Annuities are modelled, with a caution:</strong> The Suggestion Engine does test buying a lifetime annuity — several sizes, and both the nil-residual and capital-returned versions — layered on top of a deposit, never instead of one. Weigh it carefully in residential care: length of stay is typically 2&ndash;5 years, an annuity is priced on standard life expectancy, and the capital is committed for life. A <strong>RAD is usually the stronger lever</strong> — it is fully exempt from the Age Pension assets test and it is refunded — so an annuity is generally only worth considering for money left over after the deposit you are comfortable paying. Where one is proposed, the card states who owns it, what comes back and whether the income continues to a surviving partner.</li>
                                <li><strong>Optional &quot;Lower RAD, near-best legacy&quot; card:</strong> When it appears, it highlights a strategy whose projected estate is within {formatCurrency(SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD)} of the best modelled outcome but uses a lower estimated RAD paid. It is an illustrative comparison, not a personal recommendation.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Goal-first: what to optimise for (presentation only; does not re-run the engine) */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-800 mb-3">Show strategies optimised for</p>
                <div className="flex flex-wrap gap-2">
                    {(
                        [
                            { id: 'legacy', label: BEST_ESTATE_CHIP },
                            { id: 'covered', label: CASHFLOW_COVERED_CHIP },
                            { id: 'cashflow', label: 'Best cashflow' },
                            { id: 'pension', label: 'Best Age Pension' },
                        ] as const
                    ).map(({ id, label }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setOptimisationFocus(id)}
                            className={`flex-1 min-w-[140px] rounded-lg px-4 py-2.5 text-sm font-semibold transition-all border-2 ${
                                optimisationFocus === id
                                    ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm'
                                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <p className="text-xs text-slate-600 mt-3">
                    {optimisationFocus === 'covered' && 'The best estate among the plans that still pay the bills after care fees — or, where none does, whose shortfall the savings carry for the whole period. The card says which.'}
                    {optimisationFocus === 'legacy' && legacyFocusHint(factFind?.adviceFor === 'couple')}
                    {optimisationFocus === 'cashflow' && 'Strongest net cashflow from this run.'}
                    {optimisationFocus === 'pension' && 'Highest modelled Age Pension (if eligible).'}
                </p>
                {/* The projection period, stated and changeable where the figures it governs are read.
                    It also lives in the settings form; this is the same setting, not a second one. */}
                <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2 flex-wrap">
                    <label htmlFor="se-horizon" className="text-sm text-slate-700">
                        Estate figures below are modelled over
                    </label>
                    <select
                        id="se-horizon"
                        value={currentSettings.legacyYears}
                        onChange={(e) => handleHorizonChange(parseInt(e.target.value, 10))}
                        className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((y) => (
                            <option key={y} value={y}>{y} {y === 1 ? 'year' : 'years'}</option>
                        ))}
                    </select>
                    <span className="text-xs text-slate-500">
                        Nobody knows how long care will be needed — try a few periods to see how much the answer moves.
                    </span>
                </div>
            </div>

            {/* Strategy Cards */}
            {effectiveOptimisationSurface === 'covered' && coveredIsBestEstate(analysis?.recommendedOptimalStrategies) && (
                <SameAsBestEstateNote onShowBestEstate={() => setOptimisationFocus('legacy')} />
            )}
            <div className="space-y-4">
                {displayedGoalStrategies.map((strategy, index) => (
                    <React.Fragment key={`${strategy.name}-${index}`}>
                        {index === 0 && (analysis.horizonSetAside ?? []).filter((sa) => sa.metric === effectiveOptimisationSurface).map((sa) => (
                            /* Owner, 23 Sep 2026: call it out, let them open it, never promote it. */
                            <div key={`set-aside-${sa.label}`} className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-amber-400 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-1.5">
                                <p className="font-semibold text-slate-900 leading-snug">One strategy was set aside.</p>
                                <p className="text-slate-600 leading-relaxed">
                                    <span className="font-semibold text-slate-800">{getDisplayStrategyName(sa.strategy.name)}</span> would give a
                                    {sa.metric === 'legacy' ? ' larger estate' : sa.metric === 'cashflow' ? ' higher cashflow' : ' higher Age Pension'}, but it needs more time than{' '}
                                    <JargonTerm term="age and health" expandAs="dialog">{sa.warning}</JargonTerm> support, so it is not recommended.{' '}
                                    <button type="button" onClick={() => handleDeepDive(sa.strategy)} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                                        See it anyway
                                    </button>
                                </p>
                            </div>
                        ))}
                        {index === 0 && effectiveOptimisationSurface === 'legacy' && (
                            <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-blue-600 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-1.5">
                                <p className="font-semibold text-slate-900 leading-snug">Highest modelled projected estate for your selected goal.</p>
                                <p className="text-slate-600 leading-relaxed">Other cards may show a similar estate with different trade-offs — for example, less paid as a RAD up front.</p>
                            </div>
                        )}
                        {index === 0 && effectiveOptimisationSurface === 'cashflow' && (
                            <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-blue-600 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-1.5">
                                <p className="font-semibold text-slate-900 leading-snug">Strongest modelled net annual cashflow for your selected goal.</p>
                                <p className="text-slate-600 leading-relaxed">Figures below are illustrations — not guarantees.</p>
                            </div>
                        )}
                        {index === 0 && effectiveOptimisationSurface === 'pension' && (
                            <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-blue-600 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-1.5">
                                <p className="font-semibold text-slate-900 leading-snug">Highest modelled Age Pension for your selected goal (if eligible).</p>
                                <p className="text-slate-600 leading-relaxed">Figures below are illustrations — not guarantees.</p>
                            </div>
                        )}
                        {strategy.isLowerRadNearBestCard && strategy.lowerRadNearBestMeta && (
                            <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-indigo-600 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-1.5">
                                <p className="font-semibold text-slate-900 leading-snug">Lower RAD alternative</p>
                                <p className="text-slate-600 leading-relaxed">
                                    Near-identical projected estate (within{' '}
                                    <span className="font-semibold text-slate-800">{formatCurrency(strategy.lowerRadNearBestMeta.bandUsd)}</span>) with less paid as a refundable accommodation deposit (RAD):{' '}
                                    <span className="font-semibold text-slate-800">{formatCurrency(strategy.lowerRadNearBestMeta.radPaidThis)}</span>
                                    {' vs '}
                                    <span className="font-semibold text-slate-800">{formatCurrency(strategy.lowerRadNearBestMeta.radPaidTop)}</span>
                                    {strategy.lowerRadNearBestMeta.topLegacyStrategyName ? (
                                        <>
                                            {' '}
                                            for the strongest estate result in this comparison (&quot;{getDisplayStrategyName(strategy.lowerRadNearBestMeta.topLegacyStrategyName)}&quot; at{' '}
                                            {formatCurrency(strategy.lowerRadNearBestMeta.maxLegacy)}).
                                        </>
                                    ) : (
                                        <> for the strongest estate result in this comparison ({formatCurrency(strategy.lowerRadNearBestMeta.maxLegacy)}).</>
                                    )}
                                </p>
                                {/*
                                  * IF THIS PLAN COMMITS MONEY ELSEWHERE, SAY SO BEFORE CLAIMING LIQUIDITY.
                                  *
                                  * The card now ranks on money the client cannot get back, not on the
                                  * deposit alone, so it can no longer offer a plan that ties up more
                                  * than the one it is compared to. But a plan can legitimately win
                                  * with a small annuity in it, and "keeping flexibility and liquidity"
                                  * would then be telling only half the story: a deposit is refunded,
                                  * an annuity never is (owner, 2026-09-01).
                                  */}
                                {(strategy.meansTestStrategy?.purchaseAmount ?? 0) > 0 && (
                                    <p className="rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 leading-relaxed text-slate-700">
                                        Note that this plan does commit{' '}
                                        <span className="font-semibold">
                                            {formatCurrency(Number(strategy.meansTestStrategy!.purchaseAmount))}
                                        </span>{' '}
                                        to a lifetime annuity. A deposit is refunded when care ends; an annuity is not.
                                    </p>
                                )}
                                {/* Owner, 22 Sep 2026: "counting a refundable deposit at the 2% a year the provider
                                    keeps" was not clear at all. Say the plain thing: less paid in means more within
                                    reach; a deposit comes back anyway, less the retention; so the two plans differ
                                    in access to the money, not in how much is lost. */}
                                <p className="text-slate-600 leading-relaxed">
                                    Worth considering if keeping money within reach matters. Paying{' '}
                                    <span className="font-semibold text-slate-800">
                                        {formatCurrency(strategy.lowerRadNearBestMeta.radPaidTop - strategy.lowerRadNearBestMeta.radPaidThis)}
                                    </span>{' '}
                                    less as a deposit leaves that much more in your own accounts. A deposit is refunded when
                                    care ends, less the 2% a year the provider keeps for up to five years, so the two plans
                                    differ mainly in how much is out of reach for now — not in how much is lost.
                                </p>
                            </div>
                        )}
                        {strategy.isFutureEventOverlayCard && strategy.futureEventAssumptions && (
                            <div className="rounded-lg border border-slate-200 bg-white shadow-sm border-l-4 border-l-violet-600 pl-4 pr-4 py-3 text-sm text-slate-800 space-y-2">
                                <p className="font-semibold text-slate-900 leading-snug">Future event scenario</p>
                                <p className="text-slate-600 leading-relaxed">
                                    If {partnerDisplayName} enters care in Year {strategy.futureEventAssumptions.partnerEntryYear} (room{' '}
                                    {formatCurrency(strategy.futureEventAssumptions.partnerRoomPrice)}), {clientDisplayName} is modelled as{' '}
                                    {strategy.futureEventAssumptions.originalEntrantAliveAtEvent ? 'still living' : 'having passed away'} at that time.
                                </p>
                                <p className="text-slate-600 leading-relaxed">
                                    Added aged care cost estimate from that year to the end of the projection:{' '}
                                    <span className="font-semibold text-slate-800">{formatCurrency(strategy.futureEventAssumptions.additionalAnnualCareCost)} p.a.</span> (illustrative).
                                </p>
                                {strategy.futureEventAdjustmentDelta != null && strategy.futureAdjustedLegacy != null && (
                                    <p className="text-xs text-slate-600 leading-relaxed">
                                        Baseline estate {formatCurrency(strategy.futureEventMeta?.baselineLegacy ?? strategy.snapshot?.legacyValue ?? 0)}
                                        {' → '}
                                        adjustment {formatCurrency(strategy.futureEventAdjustmentDelta)}
                                        {' → '}
                                        future-aware estate {formatCurrency(strategy.futureAdjustedLegacy)}.
                                    </p>
                                )}
                                <p className="text-xs text-slate-500 leading-relaxed">Illustrative comparison only — not a primary recommendation.</p>
                            </div>
                        )}
                    <div
                        className={`bg-white border-2 rounded-lg p-6 shadow-lg ${
                            (suggestionEngineCardHighlightKey != null
                                ? strategyCardSelectionKey(strategy) === suggestionEngineCardHighlightKey
                                : index === 0)
                                ? 'border-blue-500 ring-2 ring-blue-200'
                                : 'border-gray-200'
                        }`}
                    >
                        {/* Strategy Header */}
                        <div className="flex justify-between gap-4 mb-4 items-center">
                            <div className="min-w-0 flex-1 flex flex-col gap-2">
                                {(strategy.recommendationLabels || strategy.recommendedLabel || strategy.optimisationLabel) && (
                                    <div className="flex flex-wrap items-center gap-2">
                                        {(strategy.recommendationLabels || [strategy.recommendedLabel || strategy.optimisationLabel])
                                            .filter(Boolean)
                                            .map((label, idx) => {
                                                const isLowerRadBadge =
                                                    strategy.isLowerRadNearBestCard &&
                                                    strategy.lowerRadNearBestMeta &&
                                                    label === LOWER_RAD_NEAR_BEST_LEGACY_BADGE;
                                                if (isLowerRadBadge) {
                                                    return (
                                                        <CalculationTooltip
                                                            key={`${label}-${idx}`}
                                                            title="What this card means"
                                                            valueLayout="stacked"
                                                            data={[
                                                                {
                                                                    label: 'Starting point',
                                                                    value: `We find the highest projected legacy value among all modelled strategies (${formatCurrency(strategy.lowerRadNearBestMeta!.maxLegacy)}).`,
                                                                },
                                                                {
                                                                    label: 'Close enough',
                                                                    value: `We keep strategies whose projected legacy value is within ${formatCurrency(strategy.lowerRadNearBestMeta!.bandUsd)} of that top figure.`,
                                                                },
                                                                {
                                                                    label: 'Pick',
                                                                    value:
                                                                        'When projected legacy values are that close, the numbers can look almost the same on paper—but how much capital is locked inside the RAD still matters for liquidity and flexibility.\n\n' +
                                                                        'So from that short list we show the strategy with the smallest modelled RAD payment.\n\n' +
                                                                        'If a scenario includes downsizing, our model may only put leftover sale money into the bank or the RAD (not into growth assets). Any portion that goes to the RAD is included in this RAD figure so comparisons stay consistent across strategies.',
                                                                },
                                                                {
                                                                    label: 'Please note',
                                                                    value: 'This is an extra comparison for when outcomes are similar. It does not replace the Optimal Legacy, cashflow, or pension cards.',
                                                                },
                                                            ]}
                                                        >
                                                            <span className="inline-flex items-center rounded px-2 py-0.5 text-sm font-medium leading-snug bg-blue-100 text-blue-800 cursor-help border-b border-dotted border-blue-500/60 hover:bg-blue-200/80 transition-colors">
                                                                {label}
                                                            </span>
                                                        </CalculationTooltip>
                                                    );
                                                }
                                                const isPartnerLaterBadge =
                                                    strategy.isFutureEventOverlayCard &&
                                                    strategy.futureEventMeta &&
                                                    String(label).toLowerCase().includes('partner later');
                                                if (isPartnerLaterBadge) {
                                                    const meta = strategy.futureEventMeta!;
                                                    const usedTieBreak = !!meta.usedLowerRadTieBreak;
                                                    return (
                                                        <CalculationTooltip
                                                            key={`${label}-${idx}`}
                                                            title="What this card means"
                                                            valueLayout="stacked"
                                                            data={[
                                                                {
                                                                    label: 'Scenario',
                                                                    value: `We re-score each strategy assuming ${partnerDisplayName} enters care in Year ${strategy.futureEventAssumptions?.partnerEntryYear || currentSettings.partnerEntryYear} (room ${formatCurrency(strategy.futureEventAssumptions?.partnerRoomPrice || currentSettings.partnerFutureRoomPrice)}).`,
                                                                },
                                                                {
                                                                    label: 'Score',
                                                                    value: `For each strategy we estimate the additional aged care costs from that year to the end of the projection, and subtract them from the baseline projected legacy value. Highest future-aware legacy value wins.`,
                                                                },
                                                                ...(usedTieBreak
                                                                    ? [
                                                                          {
                                                                              label: 'Tie-break',
                                                                              value:
                                                                                  `When multiple strategies are within ${formatCurrency(meta.bandUsd)} of the best future-aware legacy value (${formatCurrency(meta.maxAdjustedLegacy)}), we show the one with the lowest estimated RAD paid for flexibility.\n\n` +
                                                                                  `This card: RAD ≈ ${formatCurrency(meta.radPaidSelected)}.\n` +
                                                                                  `Top future-aware legacy strategy: RAD ≈ ${formatCurrency(meta.radPaidTopAdjusted)}.`,
                                                                          },
                                                                      ]
                                                                    : []),
                                                            ]}
                                                        >
                                                            <span className="inline-flex items-center rounded px-2 py-0.5 text-sm font-medium leading-snug bg-blue-100 text-blue-800 cursor-help border-b border-dotted border-blue-500/60 hover:bg-blue-200/80 transition-colors">
                                                                {label}
                                                            </span>
                                                        </CalculationTooltip>
                                                    );
                                                }
                                                return (
                                                    <span key={`${label}-${idx}`} className="inline-flex items-center rounded px-2 py-0.5 text-sm font-medium leading-snug bg-blue-100 text-blue-800">
                                                        {friendlyGoalLabel(label)}
                                                    </span>
                                                );
                                            })}
                                        {legacyRankByName.get(strategy.name) === 2 &&
                                            !(strategy.recommendationLabels || [strategy.recommendedLabel || strategy.optimisationLabel]).includes('Optimal Legacy') && (
                                                <span className="inline-flex items-center rounded px-2 py-0.5 text-sm font-medium leading-snug bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200/80">
                                                    2nd best estate
                                                </span>
                                            )}
                                    </div>
                                )}
                                <h3 className="text-xl font-bold text-gray-900 min-w-0 leading-snug">{getDisplayStrategyName(strategy.name, strategy.radPaidNet, strategy.radRoomPrice, secondRoomOf(strategy), moveOf(strategy), villageOf(strategy))}</h3>
                                {/* One line, only where a payoff is part of this strategy — a debt cleared
                                    is a distinct promise from a deposit paid, and a client scanning cards
                                    needs to see which ones make it without opening the deep dive. Not a
                                    section: the card's strength is one headline and one story. */}
                                {debtsClearedBy(strategy) && (
                                    <p className="text-sm font-semibold text-teal-700 mt-1 flex items-center gap-1.5">
                                        <span aria-hidden="true">✓</span>
                                        <span>Clears {debtsClearedBy(strategy)}</span>
                                    </p>
                                )}
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-3xl font-bold text-blue-600">
                                    {formatCurrency(
                                        strategy.isFutureEventOverlayCard && strategy.futureAdjustedLegacy != null
                                            ? strategy.futureAdjustedLegacy
                                            : (strategy.snapshot?.legacyValue || 0)
                                    )}
                                </div>
                                <div className="text-sm font-medium text-gray-600">
                                    {strategy.isFutureEventOverlayCard
                                        ? "What's left to the family (partner-later scenario)"
                                        : "What's left to the family"}
                                </div>
                                {/* The figure is meaningless without the period it assumes, and the
                                    period was previously only visible in a settings panel the reader
                                    had to remember opening. */}
                                <div className="text-xs text-gray-500">
                                    after {currentSettings.legacyYears} {currentSettings.legacyYears === 1 ? 'year' : 'years'}
                                </div>
                                <div className="text-xs">
                                    {/* `dialog`, not the default inline box: this sits in the card's
                                        shrink-0 figures column, where a block of prose would set the
                                        column's width and crush the strategy heading beside it. */}
                                    <JargonTerm term="how is this projected?" expandAs="dialog">
                                        {`It starts from everything owned today and runs the Annual Snapshot below forward year by year for ${currentSettings.legacyYears} ${currentSettings.legacyYears === 1 ? 'year' : 'years'}: pension and investment income in, care costs and living expenses out, investments growing at the assumed rates. Any yearly shortfall is drawn from savings (the note under the snapshot says from where). The RAD stays part of the estate because it is refunded when care ends, less the retention and anything drawn from it to pay care fees. Explain this shows the year-by-year path behind this figure.`}
                                    </JargonTerm>
                                </div>
                            </div>
                        </div>

                        {/* Asset Allocation */}
                        <div className="mb-6">
                            {/* Every block on this card states the PERIOD it describes, because they do
                                not share one and the card previously left that to be inferred. These
                                balances are a point in time (today); the snapshot below is a full
                                normalised year. Putting an undated payoff between them made three
                                different bases sit in one tile with nothing distinguishing them. */}
                            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                                <h4 className="text-lg font-semibold text-gray-800">{secondRoomOf(strategy) ? `Allocation to ${secondRoomOf(strategy)!.leadName}'s room` : 'Allocation to RAD'}</h4>
                                <span className="text-xs text-gray-500">Your balances today</span>
                            </div>
                            {/* Above the grid, because the grid is what would otherwise be misread: every
                                row shows an opening balance, and a reader with no warning takes
                                "Kept: $150,000" to mean the account is untouched. */}
                            {(() => {
                                const mv = (strategy as { facilityMove?: { stayPut?: boolean; fromRefund?: number; refundToday?: number } | null }).facilityMove;
                                if (!mv) return null;
                                return mv.stayPut ? (
                                    <p className="text-sm text-slate-700 mb-3" data-testid="card-stay-put">Nothing changes: the current deposit stays where it is and nothing is sold.</p>
                                ) : (
                                    <p className="text-sm text-slate-700 mb-3" data-testid="card-refund-first"><strong>{formatCurrency(Math.round(mv.fromRefund || 0))}</strong> comes from the refund of the current deposit first; the balances below show only what is sold on top of it.</p>
                                );
                            })()}
                            {/* Leaving a village: the exit payment arrives months after entry, so what it
                                pays is not in the balances below and the wait has a cost of its own. */}
                            {(() => {
                                const ve = (strategy as { villageExit?: { fromVillage?: number; toBank?: number; refundDate?: string; paidAtMove?: boolean; interim?: { perDay?: number; total?: number; days?: number } | null } | null }).villageExit;
                                if (!ve) return null;
                                const fromVillage = Number(ve.fromVillage) || 0;
                                const by = ve.paidAtMove ? ' (at the move)' : ve.refundDate ? ` (by ${phaseFromLabel(ve.refundDate)})` : '';
                                if (fromVillage <= 0.5) {
                                    return <p className="text-sm text-slate-700 mb-3" data-testid="card-village-bank">The village's {formatCurrency(Math.round(Number(ve.toBank) || 0))} exit payment goes to the bank when it arrives{by}; nothing of it goes to the room.</p>;
                                }
                                const perDayVillage = Number(ve.interim?.perDay) || 0;
                                const waitTotal = Number(ve.interim?.total) || 0;
                                return (
                                    <p className="text-sm text-slate-700 mb-3" data-testid="card-village-refund"><strong>{formatCurrency(Math.round(fromVillage))}</strong> comes from the village's exit payment when it arrives{by}{waitTotal > 0.5 ? <>; until then ${(Math.round(perDayVillage * 100) / 100).toFixed(2)} a day is charged on that part of the room (about {formatCurrency(Math.round(waitTotal))} in all)</> : null}. The balances below show only what is sold at entry.</p>
                                );
                            })()}
                            {debtPayoffFundingLine(strategy, factFind) && (
                                <p className="text-sm text-teal-700 mb-3">
                                    Separately, {debtPayoffFundingLine(strategy, factFind)} is used to clear debt
                                    {careStartDateFromStrategyOptions(strategyOptions) ? ' on the entry date' : ''} — after
                                    the balances shown here.
                                </p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {strategy.assetAllocation?.filter((asset) => {
                                    /*
                                     * A ROW FOR AN ACCOUNT THAT DOES NOT EXIST IS PURE NOISE.
                                     *
                                     * The generator walks every asset type the fact-find can hold, so
                                     * an empty placeholder rendered as "Shares / Managed Funds - $0
                                     * kept - No balance - Untouched": four pieces of furniture around
                                     * nothing, sitting beside the rows that matter (owner, 2026-09-01,
                                     * on this screen looking "very bla").
                                     */
                                    const kept = Math.max(0, parseFloat(String(asset.remainingAmount)) || 0);
                                    const used = Math.max(0, parseFloat(String(asset.liquidatedAmount)) || 0);
                                    return kept + used > 0;
                                }).map((asset, assetIndex) => {
                                    const keptAmount = Math.max(0, parseFloat(String(asset.remainingAmount)) || 0);
                                    const soldAmount = Math.max(0, parseFloat(String(asset.liquidatedAmount)) || 0);
                                    const opening = keptAmount + soldAmount;
                                    const soldShare = opening > 0 ? Math.round((soldAmount / opening) * 100) : 0;
                                    return (
                                    <div key={assetIndex} className="rounded-xl border border-slate-200 bg-white p-3">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <span className="min-w-0 truncate text-sm font-semibold text-slate-800">
                                                {asset.assetName}
                                            </span>
                                            <span className="shrink-0 text-sm font-bold tabular-nums text-slate-900">
                                                {formatCurrency(keptAmount)}{' '}
                                                <span className="text-[11px] font-medium text-slate-500">kept</span>
                                            </span>
                                        </div>
                                        {/*
                                          * HOW MUCH OF THIS ACCOUNT THE PLAN SPENDS, drawn rather than
                                          * left to be worked out from two figures (owner, 2026-09-01:
                                          * the screen "looks very bla"). Four grey rows of numbers made
                                          * the reader do the subtraction; the bar answers "how much of
                                          * this is gone" before any of it is read. Percentage and both
                                          * dollar figures stay, so the bar adds a reading rather than
                                          * replacing one.
                                          */}
                                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
                                            <div
                                                className={`h-full rounded-full ${soldShare >= 100 ? 'bg-rose-500' : soldShare > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                                style={{ width: `${soldShare > 0 ? Math.max(4, soldShare) : 100}%` }}
                                            />
                                        </div>
                                        <div className="mt-1.5 flex items-baseline justify-between gap-2 text-[11.5px]">
                                            <span className="text-slate-500">
                                                {opening > 0 ? `${formatCurrency(opening)} today` : 'No balance'}
                                            </span>
                                            {soldAmount > 0 ? (
                                                <span className="font-semibold text-rose-700 tabular-nums">
                                                    -{formatCurrency(soldAmount)} used ({soldShare}%)
                                                </span>
                                            ) : (
                                                <span className="font-semibold text-emerald-700">Untouched</span>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                </div>
                            {/* THE SECOND ROOM'S DEPOSIT, from the same accounts (owner, 7 Oct 2026: "where is
                                the other $310K coming from"). Taken from what the first deposit leaves, on
                                the second person's own entry date. */}
                            {(() => {
                                const sr = secondRoomRowOf(strategy);
                                if (!sr || !((Number(sr.roomPrice) || 0) > 0)) return null;
                                const names = roleNames(factFind, sr.person);
                                const lines = describeFundingKeys(factFind, sr.radFunding);
                                const funded = lines.reduce((t, l) => t + l.amount, 0);
                                const when = sr.entersLater && sr.careStartDate ? `taken when ${names.secondName} moves in, ${phaseFromLabel(sr.careStartDate)}` : 'taken with the first deposit';
                                return (
                                    <div className="mt-4" data-testid="second-room-allocation">
                                        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                                            <h4 className="text-lg font-semibold text-gray-800">Allocation to {names.secondName}&apos;s room</h4>
                                            <span className="text-xs text-gray-500">From what is left after {names.firstName}&apos;s deposit — {when}</span>
                                        </div>
                                        {lines.length === 0 ? (
                                            <p className="text-sm text-slate-600">Nothing is paid up front for {names.secondName}&apos;s room; the whole {formatCurrency(Number(sr.roomPrice) || 0)} is paid daily.</p>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {lines.map((l) => (
                                                    <div key={l.key} className="rounded-xl border border-slate-200 bg-white p-3">
                                                        <div className="flex items-baseline justify-between gap-3">
                                                            <span className="text-sm font-semibold text-gray-800">{l.label}</span>
                                                            <span className="text-sm font-bold tabular-nums text-rose-700">-{formatCurrency(l.amount)} used</span>
                                                        </div>
                                                        <p className="mt-1 text-xs text-slate-500">After {names.firstName}&apos;s deposit has come out of it.</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <p className="mt-2 text-sm text-slate-700">
                                            {formatCurrency(funded)} paid up front for {names.secondName}&apos;s {formatCurrency(Number(sr.roomPrice) || 0)} room
                                            {funded + 0.5 < (Number(sr.roomPrice) || 0) ? <> — the unpaid {formatCurrency((Number(sr.roomPrice) || 0) - funded)} is charged daily as a DAP.</> : '.'}
                                        </p>
                                    </div>
                                );
                            })()}
            </div>

            {/* The card's STORY — the same "in plain words" principle as Manual Control's plan
                recap: what this strategy actually does, told as a money FLOW the eye can follow
                (Sell -> RAD -> DAP) with one caption for the nuance chips can't carry. Built from
                this card's own rows and its own engine costs; the tiles below then hold each
                figure's why + calculation. Chips over prose: owner feedback 2026-08-24 ("too much
                visual load ... explain what's going on"). */}
            {(() => {
                const sold = (strategy.assetAllocation || []).filter(
                    (a) => (parseFloat(String(a.liquidatedAmount)) || 0) > 0,
                );
                const roomPrice = Number(strategy.radRoomPrice) || 0;
                const radPaidHere = Number(strategy.radPaidNet) || 0;
                const unpaidRoom = Math.max(0, roomPrice - radPaidHere);
                const cardCosts = (strategy.strategySpecificAgedCareCosts || {}) as { dailyAccommodationPayment?: number };
                const cardDap = Number(cardCosts.dailyAccommodationPayment) || 0;
                if (!(roomPrice > 0)) return null;
                /*
                 * The CGT the engine already deducted (radPaidNet is NET of it) but never showed
                 * (owner, 2026-08-24). Rebuilt from the card's own funding map with the SAME shared
                 * functions the engine ran - flatten to manual keys, total with
                 * computeRadFundingTotals, detail with calculateSharePartialDisposalTax - so the
                 * figure here equals the deduction to the cent.
                 */
                const manualKeys = flattenSeRadFundingToManualKeys((strategy.radFunding || {}) as never);
                const fundingTotals = computeRadFundingTotals(factFind, manualKeys, {});
                const cgtRows = Object.entries(fundingTotals.byKey)
                    .filter(([, e]) => e.cgt > 0)
                    .map(([key, e]) => {
                        const parsed = parseRadFundingAssetKey(key);
                        const located = parsed ? findAssetInFactFind(factFind, parsed.assetType, parsed.assetId) : null;
                        const asset = located?.asset;
                        const summary = asset
                            ? calculateSharePartialDisposalTax(factFind as never, asset as never, e.gross, { trustDisposal: 'distribute' })
                            : null;
                        return { label: asset?.description || 'shares', gross: e.gross, cgt: e.cgt, net: e.net, summary };
                    });
                const totalCgt = cgtRows.reduce((t, r) => t + r.cgt, 0);
                const cgtExplain =
                    cgtRows
                        .map((r) => {
                            const gain = (r.summary as { capitalGain?: number } | null)?.capitalGain;
                            const assessable = (r.summary as { assessableCapitalGain?: number } | null)?.assessableCapitalGain;
                            return (
                                `${r.label}: selling ${formatCurrency(r.gross)} realises an estimated capital gain of ` +
                                `${gain != null ? formatCurrency(gain) : 'n/a'}; after the 50% discount, ` +
                                `${assessable != null ? formatCurrency(assessable) : 'half'} is taxable, giving estimated tax of ` +
                                `${formatCurrency(r.cgt)} — so ${formatCurrency(r.net)} reaches the deposit.`
                            );
                        })
                        .join(' ') +
                    ` Worked out from the recorded cost base (or the standard assumption where none is recorded) at each owner's marginal rate — the same estimate Manual Control's funding panel shows. An accountant can confirm the exact amount.`;
                const flow = [
                    {
                        amount: radPaidHere,
                        label: 'RAD',
                        sub: unpaidRoom > 0.5 ? `refundable ${String.fromCharCode(0xb7)} ${Math.round((radPaidHere / roomPrice) * 100)}% of room` : 'refundable ' + String.fromCharCode(0xb7) + ' room paid in full',
                        tone: 'good' as const,
                    },
                    ...(unpaidRoom > 0.5
                        ? [{
                              amount: unpaidRoom,
                              label: 'unpaid',
                              sub: cardDap > 0 ? `DAP ${formatCurrency(cardDap)}/day ${String.fromCharCode(0xb7)} like rent` : 'charged daily, like rent',
                              tone: 'warn' as const,
                          }]
                        : []),
                    // The second room, where both are in care: its own deposit and its own unpaid part.
                    ...(() => {
                        const sr = secondRoomRowOf(strategy);
                        if (!sr || !((Number(sr.roomPrice) || 0) > 0)) return [];
                        const names = roleNames(factFind, sr.person);
                        const price = Number(sr.roomPrice) || 0;
                        const paid = Math.min(price, Number(sr.radPaid) || 0);
                        const unpaid = Math.max(0, price - paid);
                        const dap = Number(sr.agedCareCosts?.dailyAccommodationPayment) || 0;
                        const paidWhen = sr.entersLater && sr.careStartDate ? `, paid ${phaseFromLabel(sr.careStartDate)}` : '';
                        return [
                            ...(paid > 0.5 ? [{ amount: paid, label: `RAD ${String.fromCharCode(0xb7)} ${names.secondName}'s room`, sub: `refundable ${String.fromCharCode(0xb7)} ${Math.round((paid / price) * 100)}% of room${paidWhen}`, tone: 'good' as const }] : []),
                            ...(unpaid > 0.5 ? [{ amount: unpaid, label: `unpaid ${String.fromCharCode(0xb7)} ${names.secondName}'s room`, sub: dap > 0 ? `DAP ${formatCurrency(dap)}/day ${String.fromCharCode(0xb7)} like rent` : 'charged daily, like rent', tone: 'warn' as const }] : []),
                        ];
                    })(),
                ];
                return (
                    <PlainWordsCard eyebrow="What this strategy does" className="mb-6">
                        {(() => {
                            const cov = (analysis?.recommendedOptimalStrategies || []).find((r) => r.label === CASHFLOW_COVERED_LABEL && r.strategy?.name === strategy.name)?.cashflowCovered;
                            return cov ? (
                                <p className={`mb-2.5 rounded-lg border-l-4 py-2 pl-3 pr-2 text-[13px] leading-relaxed border border-slate-200 bg-white text-slate-700 ${cov.basis === 'covered' ? 'border-l-emerald-400' : cov.basis === 'carried' ? 'border-l-blue-400' : 'border-l-amber-400'}`}>
                                    <span className="font-semibold">{CASHFLOW_COVERED_CHIP}.</span> {cashflowCoveredNote(cov, currentSettings.legacyYears || legacyYears || 5, formatCurrency)}
                                </p>
                            ) : null;
                        })()}
                        <MoneyFlow steps={flow} />
                        {/*
                          * THE PLAN IN ONE SENTENCE (owner, 2026-09-22). The plan summary rail holds the
                          * walk-in position in engine mode, so this card is the only place the plan is
                          * described: the same In-brief template Manual Control's rail uses, measured
                          * against the same no-plan baseline this page measures everything against —
                          * pension, cashflow (net of a full year of care) and the estate at the horizon.
                          */}
                        {(() => {
                            const basePension = agedCareBaseline?.pension;
                            const baseCashFlow = agedCareBaseline?.cashFlowAfterCare;
                            const fin = strategy.strategyFinancials as { socialSecurity?: number; cashFlow?: number } | undefined;
                            const costs = strategy.strategySpecificAgedCareCosts as { dailyAccommodationPayment?: number; totalDailyCost?: number } | undefined;
                            if (!fin || !costs || basePension == null || baseCashFlow == null) return null;
                            const secondRoomRow = (strategy as { secondRoom?: { agedCareCosts?: { dailyAccommodationPayment?: number; totalDailyCost?: number }; entersLater?: boolean } }).secondRoom;
                            // The first year: the second room only where both enter together.
                            const secondCosts = secondRoomRow && !secondRoomRow.entersLater ? secondRoomRow.agedCareCosts : undefined;
                            const dailyDap = (Number(costs.dailyAccommodationPayment) || 0) + (Number(secondCosts?.dailyAccommodationPayment) || 0);
                            const householdDaily = householdDailyCost(costs, secondCosts);
                            const phaseTwo = (strategy as { phaseTwo?: { fromDate?: string; householdDailyCost?: number } | null }).phaseTwo;
                            const phaseTwoDaily = strategyPhaseTwoDailyCost({ phaseTwo });
                            const phaseNames = { ...roleNames(factFind, (secondRoomRow as { person?: 'client' | 'partner' } | undefined)?.person), fromLabel: phaseFromLabel(phaseTwo?.fromDate || '') };
                            const cashflowAfter = (Number(fin.cashFlow) || 0) - householdDaily * 365;
                            const rows = strategy.projections as Array<{ netWorth?: number }> | undefined;
                            const planLegacy = rows && rows.length > 0 ? rows[rows.length - 1]?.netWorth ?? null : null;
                            return (
                                <p className="mt-2.5 text-[13px] leading-relaxed text-slate-700">
                                    <RecapClauses
                                        clauses={buildPlanRecapClauses({
                                            roomFullyPaid: dailyDap <= 0.005,
                                            dapAnnual: dailyDap * 365,
                                            careAnnual: householdDaily * 365,
                                            pensionDelta: (Number(fin.socialSecurity) || 0) - basePension,
                                            cashflowDelta: cashflowAfter - baseCashFlow,
                                            cashflowAfter,
                                            legacyDelta:
                                                planLegacy != null && agedCareBaseline?.legacyValue != null
                                                    ? planLegacy - agedCareBaseline.legacyValue
                                                    : null,
                                            legacyYears: Number(legacyYears) || 0,
                                        })}
                                    />
                                    {phaseTwoDaily != null && (
                                        <> <span data-testid="card-phase-two">{recapPhaseLine(phaseNames, Number(costs.totalDailyCost) || 0, phaseTwoDaily)}</span></>
                                    )}
                                </p>
                            );
                        })()}
                        {(() => {
                            const horizonNote = engineStrategyHorizonWarning(strategy, factFind);
                            return horizonNote ? (
                                <p className="mt-2.5 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                                    <span className="font-semibold">Worth knowing.</span> {horizonNote}
                                </p>
                            ) : null;
                        })()}
                        <p className="mt-2.5 text-xs leading-relaxed text-slate-500">
                            {sold.length > 0 && (
                                <>
                                    Funded by the sales shown above
                                    {totalCgt > 0 && (
                                        <>
                                            {' '}— an estimated{' '}
                                            <span className="font-semibold text-slate-700">{formatCurrency(totalCgt)}</span> in{' '}
                                            <JargonTerm term="capital gains tax" /> is deducted before the money reaches the
                                            deposit (<JargonTerm term="how is it worked out?">{cgtExplain}</JargonTerm>)
                                        </>
                                    )}
                                    {'. '}
                                </>
                            )}
                            A <JargonTerm term="RAD" /> is refunded when care ends, less 2% a year for the first five
                            years{unpaidRoom > 0.5 ? (
                                <>
                                    ; the <JargonTerm term="DAP" /> is inside Total Aged Care Costs below and none of it
                                    comes back
                                </>
                            ) : null}. Tap any tile below for why its figure is what it is.
                        </p>
                        {/*
                          * ON THE CARD, not only inside "Explain this" (owner, 2026-09-01: "it wasn't
                          * until I clicked on 'Explain this' that I realised that this is a reverse
                          * mortgage").
                          *
                          * A reverse mortgage makes no repayments, so it puts nothing in the cashflow
                          * tiles below — which is exactly what let it top the cashflow ranking and
                          * exactly what makes it invisible. The one line that has to reach a reader
                          * who never opens the explanation is that there IS a cost and where it went.
                          */}
                        {(() => {
                            const er = (strategy as { equityRelease?: Record<string, unknown> }).equityRelease;
                            if (!er || !(Number(er.loanAmount) > 0)) return null;
                            const grown = Number(er.projectedBalance) || 0;
                            const drawn = Number(er.loanAmount) || 0;
                            const yrs = Number(er.years) || 0;
                            return (
                                <p className="mt-2.5 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-xs leading-relaxed text-slate-700">
                                    <span className="font-semibold">
                                        This is a {er.kind === 'heas' ? 'Home Equity Access Scheme loan' : 'reverse mortgage'}.
                                    </span>{' '}
                                    There are no repayments, so none of its cost appears in the cashflow below.
                                    {grown > drawn && yrs > 0 && (
                                        <> The debt compounds from {formatCurrency(drawn)} to{' '}
                                        <span className="font-semibold">{formatCurrency(grown)}</span> over {yrs}{' '}
                                        {yrs === 1 ? 'year' : 'years'} and is settled out of the house.</>
                                    )}{' '}
                                    <span className="font-semibold">Explain this</span> sets out how it works and what to watch.
                                </p>
                            );
                        })()}
                    </PlainWordsCard>
                );
            })()}
            {/* Annual Snapshot — extracted so each tile can carry its own why + calculation.
                See AnnualSnapshot.tsx: the six figures, hover tooltips kept, tap layer added. */}
            <AnnualSnapshot strategy={strategy} agedCareCosts={agedCareCosts} factFind={factFind} baseline={snapshotBaseline} />
                {/* Action Buttons - Streamlined Layout */}
                <div className="mt-4 space-y-2">
                    {/* "Negative cashflow will be drawn down from liquid assets" used to sit here, and it
                        answered a question nobody was asking. What a client wants to know — asked three
                        separate times in one meeting on 2026-08-21 — is WHEN the money runs out and what
                        pays for the care after that. The answer already existed, in the Insight Panel,
                        which that client had closed within seconds of arriving. So it belongs here. */}
                    <StrategyFundingNote strategy={strategy} />
                <div className="flex gap-3">
                    {/* The same plain-words reconciliation Manual Control has. The tile expanders
                        above explain what each FEE is; this explains what THIS PLAN does — pension,
                        cashflow and estate, each with the reason it moved (owner, 2026-08-27: the
                        Manual Control explanations "felt a bit more personalised"). Same component,
                        driven by the strategy's own figures. */}
                    <Button
                        onClick={() => setExplainStrategy(strategy)}
                        variant="secondary"
                        size="sm"
                        icon={BookOpen}
                        className="flex-1"
                    >
                        Explain this
                    </Button>
                    <Button
                        onClick={() => setRiskStrategy(strategy)}
                        variant="secondary"
                        size="sm"
                        icon={ShieldAlert}
                        className="flex-1"
                    >
                        What could go wrong
                    </Button>
                    <Button
                        onClick={() => {
                            saveSelectedStrategy(strategy);
                        }}
                            variant="primary"
                        size="sm"
                        icon={CheckCircle}
                        className="flex-1"
                    >
                        {suggestionEngineCardHighlightKey != null &&
                        strategyCardSelectionKey(strategy) === suggestionEngineCardHighlightKey
                            ? 'Selected ✓'
                            : 'Select Strategy'}
                    </Button>
            </div>
                {suggestionEngineCardHighlightKey != null &&
                    strategyCardSelectionKey(strategy) === suggestionEngineCardHighlightKey && (
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-slate-200/70 border-l-4 border-l-emerald-500 bg-white px-3 py-2 text-sm text-emerald-900 shadow-sm">
                            <p className="min-w-0 flex-1">
                                <span className="font-semibold">This is your selected strategy.</span> It is saved with your
                                client file and the reports will use it. To adjust the details, switch to Manual
                                Control — your selection carries over.
                            </p>
                            {/* The Statement of Advice is generated from HERE, for the chosen strategy only
                                (owner, 2 Oct 2026) — it was a fourth button on every card. */}
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedStrategyForSOA(strategy);
                                    setShowSOAView(true);
                                }}
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
                            >
                                Generate your Statement of Advice →
                            </button>
                        </div>
                    )}
        </div>
                    </div>
                    </React.Fragment>
                ))}
            </div>

            {/* Goal leaders vs full simulation list */}
            {analysis.strategies && analysis.strategies.length > 0 && (
                <div className="mt-4 mx-auto flex w-full max-w-2xl justify-center px-2">
                    <div className="flex w-full gap-3">
                        {analysis.strategies.length > 3 && (
                            <Button
                                type="button"
                                onClick={() => setShowAllStrategiesTable(true)}
                                variant="secondary"
                                className="flex-1 basis-0"
                            >
                                Analyse more
                            </Button>
                        )}
                        <Button
                            type="button"
                            onClick={() => setShowGoalLeadersModal(true)}
                            variant="primary"
                            className="flex-1 basis-0"
                        >
                            Compare all strategies
                        </Button>
                    </div>
                </div>
            )}

            {/* Goal leaders only: optimal legacy / cashflow / pension + optional lower RAD + future event */}
            {showGoalLeadersModal && analysis.strategies && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowGoalLeadersModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                        <GradientText>Compare all strategies</GradientText>
                                    </h2>
                                    <p className="text-sm text-slate-600 mt-1.5">
                                        Highlight rows only — hover strategy names for funding detail. Full list: <strong>Analyse more</strong>.{' '}
                                        <span className="text-slate-500">Projections, not guarantees.</span>
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowGoalLeadersModal(false)}
                                    className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
                        </div>
                        <div className="flex-1 overflow-auto px-6 py-5 min-h-0">
                            {goalComparisonRows.length === 0 ? (
                                <p className="text-sm text-slate-600 py-10 text-center">
                                    No goal comparison rows are available for this analysis run.
                                </p>
                            ) : (
                                <table className="w-full text-sm border-collapse rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                    <thead>
                                        <tr className="bg-slate-100 text-slate-800">
                                            <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Goal</th>
                                            <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy</th>
                                            <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Projected estate</th>
                                            <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Net cashflow</th>
                                            <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {goalComparisonRows.map(({ goalLabel, strategy }, idx) => {
                                            const estateDisplay =
                                                strategy.isFutureEventOverlayCard && strategy.futureAdjustedLegacy != null
                                                    ? strategy.futureAdjustedLegacy
                                                    : strategy.snapshot?.legacyValue ?? 0;
                                            const estateNote =
                                                strategy.isFutureEventOverlayCard && strategy.futureAdjustedLegacy != null
                                                    ? 'Future-aware scenario'
                                                    : null;
                                            const isDownsize =
                                                strategy.strategyOptions?.downsizeHome ||
                                                (strategy.name && strategy.name.startsWith('Downsize'));
                                            const downsizeTooltipData = isDownsize ? getDownsizeStrategyTooltipData(factFind, strategy) : null;
                                            const radTooltipData = !downsizeTooltipData ? getRadFundingTooltipData(strategy.radFunding as unknown as RadFundingBag) : null;
                                            const fundingTooltipData = downsizeTooltipData || radTooltipData;
                                            const fundingTooltipTitle = downsizeTooltipData ? 'How this strategy is funded' : 'RAD funded from';
                                            return (
                                                <tr
                                                    key={`${goalLabel}-${strategy.name}-${idx}`}
                                                    onClick={() => {
                                                        setShowGoalLeadersModal(false);
                                                        handleDeepDive(strategy);
                                                    }}
                                                    className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                                                >
                                                    <td className="px-4 py-3 font-medium text-slate-800 align-top">{goalLabel}</td>
                                                    <td className="px-4 py-3 font-medium text-slate-900 align-top">
                                                        {fundingTooltipData ? (
                                                            <CalculationTooltip title={fundingTooltipTitle} data={fundingTooltipData}>
                                                                <span className="cursor-help">{getDisplayStrategyName(strategy.name, strategy.radPaidNet, strategy.radRoomPrice, secondRoomOf(strategy), moveOf(strategy), villageOf(strategy))}</span>
                                                            </CalculationTooltip>
                                                        ) : (
                                                            getDisplayStrategyName(strategy.name, strategy.radPaidNet, strategy.radRoomPrice, secondRoomOf(strategy), moveOf(strategy), villageOf(strategy))
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-slate-800 align-top">
                                                        <div>{formatCurrency(estateDisplay)}</div>
                                                        {estateNote && <div className="text-xs font-normal text-slate-500 mt-0.5">{estateNote}</div>}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(strategy.snapshot?.netCashflow ?? 0)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(strategy.snapshot?.pension ?? 0)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end shrink-0">
                            <button
                                type="button"
                                onClick={() => setShowGoalLeadersModal(false)}
                                className="px-5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 shadow-sm transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full strategies table (modal) — every simulated combination; opened from Analyse more */}
            {showAllStrategiesTable && analysis.strategies && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAllStrategiesTable(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80" onClick={e => e.stopPropagation()}>
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                        <GradientText>All modelled strategies</GradientText>
                                    </h2>
                                    <p className="text-sm text-slate-600 mt-1.5">
                                        Every simulated combination — click a row to open Deep Dive. Sort by projected estate, cashflow, or pension.
                                        {(analysis.allSimulatedStrategies && analysis.allSimulatedStrategies.length > 0) && (
                                            <span className="block mt-1 text-slate-500">
                                                {analysis.allSimulatedStrategies.length} simulated strategies
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button type="button" onClick={() => setShowAssumptionsModal(true)} className="text-sm text-blue-600 hover:underline font-medium">
                                        Assumptions
                                    </button>
                                    <label className="text-sm text-slate-600">Sort by</label>
                                    <select
                                        value={allStrategiesSortBy}
                                        onChange={(e) => setAllStrategiesSortBy(e.target.value)}
                                        className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
                                    >
                                        <option value="legacy">Projected estate</option>
                                        <option value="cashflow">Net cashflow</option>
                                        <option value="pension">Age pension</option>
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setShowAllStrategiesTable(false)}
                                        className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
                        </div>

                        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                            <div className="overflow-auto flex-1 px-6 py-5">
                                <table className="w-full text-sm border-collapse rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                    <thead>
                                        <tr className="bg-slate-100 text-slate-800">
                                            <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy</th>
                                            <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Projected estate</th>
                                            <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Net cashflow</th>
                                            <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableStrategies.map((s, idx) => {
                                            const isDownsize = s.strategyOptions?.downsizeHome || (s.name && s.name.startsWith('Downsize'));
                                            const downsizeTooltipData = isDownsize ? getDownsizeStrategyTooltipData(factFind, s) : null;
                                            const radTooltipData = !downsizeTooltipData ? getRadFundingTooltipData(s.radFunding as unknown as RadFundingBag) : null;
                                            const tooltipData = downsizeTooltipData || radTooltipData;
                                            const tooltipTitle = downsizeTooltipData ? 'How this strategy is funded' : 'RAD funded from';
                                            return (
                                                <tr
                                                    key={`${s.name}-${idx}`}
                                                    onClick={() => {
                                                        setShowAllStrategiesTable(false);
                                                        handleDeepDive(s);
                                                    }}
                                                    className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                                                >
                                                    <td className="px-4 py-3 font-medium text-slate-900 align-top">
                                                        {tooltipData ? (
                                                            <CalculationTooltip title={tooltipTitle} data={tooltipData}>
                                                                <span className="cursor-help">{s.name}</span>
                                                            </CalculationTooltip>
                                                        ) : (
                                                            s.name
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-slate-800">
                                                        {formatCurrency(s.snapshot?.legacyValue ?? 0)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(s.snapshot?.netCashflow ?? 0)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(s.snapshot?.pension ?? 0)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end shrink-0">
                            <button
                                type="button"
                                onClick={() => setShowAllStrategiesTable(false)}
                                className="px-5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 shadow-sm transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Back to Dashboard Button - After all strategies */}
            {analysis && analysis.strategies && analysis.strategies.length > 0 && onNavigateToDashboard && (
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={onNavigateToDashboard}
                        className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 flex items-center gap-2 font-semibold shadow-md hover:shadow-lg"
                    >
                        <ArrowLeft className="h-5 w-5" />
                        Back to Dashboard
                    </button>
                </div>
            )}

            {riskStrategy && (
                <StrategyRisksPanel
                    strategyName={getDisplayStrategyName(riskStrategy.name, riskStrategy.radPaidNet, riskStrategy.radRoomPrice, secondRoomOf(riskStrategy), moveOf(riskStrategy), villageOf(riskStrategy))}
                    onClose={() => setRiskStrategy(null)}
                    risks={buildStrategyRisks(riskKindsFromAgedCareStrategy(riskStrategy as never), {
                        horizonWarning: engineStrategyHorizonWarning(riskStrategy, factFind),
                    })}
                />
            )}

            {/* Deep Dive Modal */}
            {deepDiveModal && selectedScenario && (
                <DeepDiveModal
                    selectedScenario={selectedScenario}
                    factFind={factFind}
                    agedCareCosts={agedCareCosts}
                    legacyYears={legacyYears}
                    analysis={analysis}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onSelectStrategy={() => { saveSelectedStrategy(selectedScenario); setDeepDiveModal(false); }}
                    onClose={() => setDeepDiveModal(false)}
                    applyRadFundingToFactFind={applyRadFundingToFactFind}
                    careStartDate={careStartDateFromStrategyOptions(strategyOptions)}
                    radDrawdown={radDrawdownFromStrategyOptions(strategyOptions)}
                    residentialFeeArrangements={feeArrangementsFromStrategyOptions(strategyOptions)}
                />
            )}


            {/* Save Warning Modal */}
            {showSaveWarning && createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-t-lg">
                            <div className="flex items-center">
                                <AlertTriangle className="h-6 w-6 mr-3" />
                                <h3 className="text-lg font-semibold">Save Current Strategy?</h3>
                            </div>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700 mb-4">
                                You have optimisation results that will be lost when changing settings.
                            </p>
                            <p className="text-gray-600 mb-6">
                                Would you like to save your current recommended strategy?
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => handleSaveWarningResponse('cancel')}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleSaveWarningResponse('continue')}
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                                >
                                    Continue
                                </button>
                                <button
                                    onClick={() => handleSaveWarningResponse('save')}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                >
                                    Save Strategy
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Settings Modal */}
            {showSettingsModal && createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-t-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Settings className="h-6 w-6 mr-3" />
                                    <h3 className="text-lg font-semibold">Edit Settings</h3>
                                </div>
                                <button
                                    onClick={() => setShowSettingsModal(false)}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <SettingsForm 
                                currentSettings={currentSettings}
                                onSave={handleSettingsSave}
                                onCancel={() => setShowSettingsModal(false)}
                                factFind={factFind}
                                scenarioLivingExpenses={scenarioLivingExpensesOr(agedCareCosts?.scenarioLivingExpenses, factFind?.expenses?.livingExpenses)}
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

/**
 * The nominated care start date off the shared strategy options, or `undefined` if none was set.
 *
 * Read defensively because `strategyOptions` is a loose bag on this panel's props — a date that failed
 * to be a `YYYY-MM-DD` string is treated as no date at all, which is the same "care is already under
 * way" the engine assumes for an absent one.
 */
/** The second room the Analyser set, where both of a couple are entering care — priced in every candidate. */
function secondRoomFromStrategyOptions(strategyOptions?: Record<string, unknown>): SecondRoomForEngine | null {
    const v = strategyOptions?.secondRoom as SecondRoomForEngine | undefined;
    if (!v || !(Number(v.roomPrice) > 0) || (v.person !== 'client' && v.person !== 'partner')) return null;
    return { person: v.person, roomPrice: Number(v.roomPrice), ...(v.careStartDate ? { careStartDate: v.careStartDate } : {}), ...(v.extraServiceAnnual ? { extraServiceAnnual: Number(v.extraServiceAnnual) } : {}), entersLater: !!v.entersLater };
}
type SecondRoomForEngine = { person: 'client' | 'partner'; roomPrice: number; careStartDate?: string; extraServiceAnnual?: number; entersLater?: boolean };
/** A change of home, as the Analyser set it (see the engine's FacilityMoveInput). */
type FacilityMoveForEngine = { person: 'client' | 'partner'; moveDate: string; newRoomPrice: number; extraServiceAnnual?: number; refundToday: number; currentRoomPrice: number; currentDapAnnual: number; grandfathered: boolean; currentSchedule?: Record<string, unknown> };
function facilityMoveFromStrategyOptions(strategyOptions?: Record<string, unknown>): FacilityMoveForEngine | null {
    const v = strategyOptions?.facilityMove as FacilityMoveForEngine | undefined;
    if (!v || !(Number(v.newRoomPrice) > 0) || !v.moveDate) return null;
    return v;
}
/** Leaving a retirement village, as the Analyser set it (see the engine's VillageExitInput). */
type VillageExitForEngine = { person: 'client' | 'partner'; leaveDate: string; refundDate: string; entitlement: number };
function villageExitFromStrategyOptions(strategyOptions?: Record<string, unknown>): VillageExitForEngine | null {
    const v = strategyOptions?.villageExit as VillageExitForEngine | undefined;
    if (!v || !(Number(v.entitlement) > 0) || !v.leaveDate || !v.refundDate) return null;
    return v;
}

function careStartDateFromStrategyOptions(strategyOptions?: Record<string, unknown>): string | undefined {
    const value = strategyOptions?.careStartDate;
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

/**
 * Whether the provider lets fees be drawn from the deposit, as answered on the Analyser's costs form.
 * Read the same defensive way as the entry date; anything unrecognised is "not answered", which the
 * engine treats as the usual written agreement ('fees').
 */
/** The fee arrangements the Analyser chose for a no-worse-off resident, if any — read the same way as the deposit arrangement. */
function feeArrangementsFromStrategyOptions(strategyOptions?: Record<string, unknown>): '2014' | '2025' | undefined {
    const value = strategyOptions?.residentialFeeArrangements;
    return value === '2014' || value === '2025' ? value : undefined;
}

function radDrawdownFromStrategyOptions(strategyOptions?: Record<string, unknown>): 'fees' | 'dap' | 'none' | undefined {
    const value = strategyOptions?.radDrawdown;
    return value === 'fees' || value === 'dap' || value === 'none' ? value : undefined;
}

// INTEGRATED METHOD: Generate scenarios and call manual control directly
async function generateScenariosAndCallManualControl(
    factFind: DraftFactFind,
    agedCareCosts: AgedCareCosts,
    goals: SuggestionEngineGoals,
    legacyYears: number,
    coupleValidation: CoupleValidation,
    setProgressCallback: ((progress: { current: number; total: number }) => void) | null = null,
    /**
     * When the client moves into care (`YYYY-MM-DD`), or absent for care already under way — the same
     * date the Aged Care Analyser's "when does care start?" control sets.
     *
     * Applied to every scenario alike, because it describes the case rather than any one funding
     * strategy: candidates that assumed different entry dates would be ranked against each other on
     * different sets of years.
     */
    careStartDate?: string,
    /** A provider's quoted annual annuity income, re-ranking the whole field on the real number. */
    annuityIncomeOverride: number | null = null,
    /** Whether fees may be drawn from the deposit — a fact about the case, applied to every candidate. */
    radDrawdown?: 'fees' | 'dap' | 'none',
    /** `'2025'` = a no-worse-off resident opting in to the 1 November 2025 fee arrangements; likewise a fact about the case. */
    residentialFeeArrangements?: '2014' | '2025',
    /** The second room, where both of a couple are entering care; every candidate prices it. */
    secondRoom: SecondRoomForEngine | null = null,
    /** A change of home: every candidate but "stay put" moves on its date. */
    facilityMove: FacilityMoveForEngine | null = null,
    /** Leaving a retirement village: the exit payment pays the room first when it arrives. */
    villageExit: VillageExitForEngine | null = null,
): Promise<AnalysisResult> {
    // The same inputs give the same answer: a run this session already did is handed straight back,
    // which is what makes re-opening a saved plan immediate (engineAnalysisCache.ts).
    const cacheKey = analysisCacheKey([factFind, agedCareCosts, goals, legacyYears, coupleValidation, careStartDate, annuityIncomeOverride, radDrawdown, residentialFeeArrangements, secondRoom, facilityMove, villageExit]);
    const remembered = cachedAnalysis<AnalysisResult>(cacheKey);
    if (remembered) {
        if (setProgressCallback) setProgressCallback({ current: remembered.strategies.length, total: remembered.strategies.length });
        return remembered;
    }

    // Generate scenarios from the original factFind (evaluateAgedCareRadFundingScenarios below runs
    // server-side over the network, so factFind is only ever read here, never mutated in place).
    const scenarios = generateScenarios(factFind, agedCareCosts, coupleValidation, annuityIncomeOverride, secondRoom, facilityMove, villageExit);

    // Update total scenarios for progress tracking
    if (setProgressCallback) {
        setProgressCallback({ current: 0, total: scenarios.length });
    }

    const futureEventConfig = {
        // The "partner later" overlay is the old approximation of a second room; with the second
        // room modelled in the engine itself (7 Oct 2026) it is switched off.
        enabled: !!goals?.enablePartnerFutureEvent && !secondRoom,
        partnerEntryYear: parseInt(String(goals?.partnerEntryYear), 10) || 3,
        partnerRoomPrice: parseFloat(String(goals?.partnerFutureRoomPrice)) || 0,
        originalEntrantAliveAtEvent: !!goals?.originalEntrantAliveAtPartnerEntry,
    };

    // One request evaluates every scenario against the engine server-side (see
    // server/src/rest/calculation/suggestionEngine/agedCare.ts) instead of one request per scenario
    // (previously up to ~150-300 scenarios).
    const evaluatedStrategies: Strategy[] = await calculationApi.evaluateAgedCareRadFundingScenarios(factFind, agedCareCosts, scenarios, legacyYears, facilityMove ? undefined : careStartDate, radDrawdown, residentialFeeArrangements, undefined, secondRoom, facilityMove, villageExit);
    if (setProgressCallback) {
        setProgressCallback({ current: scenarios.length, total: scenarios.length });
    }

    // The future-life-event overlay is a pure heuristic on top of the already-evaluated
    // strategy.snapshot/.name/.radFunding (no re-projection needed), so it stays a client-side
    // post-processing step, unchanged from the original per-scenario loop.
    const strategies: Strategy[] = evaluatedStrategies.map((strategyRow) => {
        if (futureEventConfig.enabled && futureEventConfig.partnerRoomPrice > 0) {
            const futureEventResult = computeFutureEventAdjustedLegacy({
                strategy: strategyRow as unknown as EvaluatedStrategy,
                legacyYears,
                partnerEntryYear: futureEventConfig.partnerEntryYear,
                partnerRoomPrice: futureEventConfig.partnerRoomPrice,
                originalEntrantAliveAtEvent: futureEventConfig.originalEntrantAliveAtEvent,
                baseAgedCareCosts: (strategyRow.strategySpecificAgedCareCosts as AgedCareCosts) || agedCareCosts,
            });
            if (futureEventResult) {
                return {
                    ...strategyRow,
                    futureAdjustedLegacy: futureEventResult.adjustedLegacy,
                    futureEventAdjustmentDelta: futureEventResult.adjustmentDelta,
                    futureEventAssumptions: futureEventResult.assumptions,
                };
            }
        }
        return strategyRow;
    });

    // Primary ranking and "Analyse more" legacy sort use baseline projected estate.
    // Future-event-adjusted legacy is computed per strategy for the optional fourth card only.
    const getBaselineLegacyRankingValue = (strategy: Strategy | null | undefined) => strategy?.snapshot?.legacyValue || 0;

    // Keep full list for "Analyse more" (all simulated strategies), sorted by baseline legacy
    const allSimulatedStrategies = [...strategies].sort((a, b) => getBaselineLegacyRankingValue(b) - getBaselineLegacyRankingValue(a));

    // Phase 2: Keep only the best funding order per scenario group (by legacy value) for top cards & recommendations
    const byGroup: Record<string, Strategy[]> = {};
    const ungrouped: Strategy[] = [];
    strategies.forEach(s => {
        if (s.groupKey) {
            const key = String(s.groupKey);
            if (!byGroup[key]) byGroup[key] = [];
            byGroup[key].push(s);
        } else {
            ungrouped.push(s);
        }
    });
    const bestPerGroup = Object.values(byGroup).map(group =>
        group.reduce((best, s) => getBaselineLegacyRankingValue(s) > getBaselineLegacyRankingValue(best) ? s : best)
    );
    /*
     * Strip the FUNDING ORDER and nothing else. This was "any trailing parenthesised group", which
     * also removed the annuity's residual-capital variant and the debt-repayment basis — leaving two
     * genuinely different strategies under one name, one showing a $200,000 larger estate than the
     * other (seen in the compare-all table, 2026-08-27). It mutates `s.name`, so the collision
     * followed the strategy everywhere, including into the SOA.
     */
    bestPerGroup.forEach(s => {
        s.name = stripFundingOrderSuffix(s.name || '');
    });
    strategies.length = 0;
    strategies.push(...ungrouped, ...bestPerGroup);

    // Rank strategies by baseline legacy (today's projection); future-aware winner is the optional fourth card.
    strategies.sort((a, b) => getBaselineLegacyRankingValue(b) - getBaselineLegacyRankingValue(a));

    // === MARK OPTIMAL STRATEGY (no duplicate entry) ===
    // Label the top strategy so the UI can show "Age Pension Optimised" / "Cashflow Maximised" as a badge
    // while keeping the strategy approach (e.g. "Pay 40% RAD, Keep Home") as the main title.
    try {
        if (strategies.length > 0) {
            let bestPensionStrategy: Strategy | null = null;
            let bestCashflowStrategy: Strategy | null = null;

            strategies.forEach(s => {
                if (!bestPensionStrategy || (s.snapshot?.pension || 0) > (bestPensionStrategy.snapshot?.pension || 0)) {
                    bestPensionStrategy = s;
                }
                if (!bestCashflowStrategy || (s.snapshot?.netCashflow || -Infinity) > (bestCashflowStrategy.snapshot?.netCashflow || -Infinity)) {
                    bestCashflowStrategy = s;
                }
            });

            const hasPensionEligibility = ((bestPensionStrategy as Strategy | null)?.snapshot?.pension || 0) > 0;
            const base: Strategy | null = hasPensionEligibility ? bestPensionStrategy : bestCashflowStrategy;
            const optimisationLabel = hasPensionEligibility ? 'Age Pension Optimised' : 'Cashflow Maximised';

            if (base) {
                (base as Strategy).optimisationLabel = optimisationLabel;
            }
        }
    } catch (e) {
        console.warn('Optimisation label assignment skipped due to error:', e);
    }

    // Deduplicate by name (keep first = highest legacy) so we never show duplicate strategy tiles
    const seenNames = new Set<string>();
    const deduped = strategies.filter(s => {
        if (seenNames.has(s.name || '')) return false;
        seenNames.add(s.name || '');
        return true;
    });
    strategies.length = 0;
    strategies.push(...deduped);


    // === RECOMMENDED OPTIMAL STRATEGIES (top 3: legacy, cashflow, age pension; no duplicates; pension fallback) ===
    const recommendedOptimalStrategies: RecommendedOptimalStrategyRow[] = [];
    const horizonSetAside: HorizonSetAside<Strategy>[] = [];
    if (strategies.length > 0) {
        // Use the full simulated set for cashflow/pension optimisation so recommendations
        // align with the "All modelled strategies" table.
        const fullUniverse = (allSimulatedStrategies && allSimulatedStrategies.length > 0)
            ? allSimulatedStrategies
            : strategies;
        /*
         * A STRATEGY THAT NEEDS MORE TIME THAN THE CLIENT HAS IS NEVER CROWNED (owner, 23 Sep 2026:
         * "I definitely don't think we should promote it as the best result"). It stays in the field,
         * costed and openable, with its warning on the card; it just cannot be a leader. Where one
         * would have won, that is recorded below and disclosed, so nothing is hidden either.
         *
         * Only the LEADERS are judged here. The full field is untouched, so no figure moves.
         */
        const flagged = (s: Strategy) => engineStrategyHorizonWarning(s, factFind) != null;
        const eligibleStrategies = strategies.filter((s) => !flagged(s));
        const recommendationUniverse = fullUniverse.filter((s) => !flagged(s));
        const bestLegacy = eligibleStrategies[0] ?? strategies[0]!; // already sorted by baseline legacy desc
        const bestCashflow = (recommendationUniverse.length ? recommendationUniverse : fullUniverse).reduce((best, s) =>
            (s.snapshot?.netCashflow ?? -Infinity) > (best?.snapshot?.netCashflow ?? -Infinity) ? s : best
        );
        const bestPension = (recommendationUniverse.length ? recommendationUniverse : fullUniverse).reduce((best, s) =>
            (s.snapshot?.pension ?? 0) > (best?.snapshot?.pension ?? 0) ? s : best
        );
        // The set-asides: a flagged strategy that beats the chosen leader on that ranking's metric.
        const metricOf = { legacy: (s: Strategy) => Number(s.snapshot?.legacyValue ?? -Infinity), cashflow: (s: Strategy) => Number(s.snapshot?.netCashflow ?? -Infinity), pension: (s: Strategy) => Number(s.snapshot?.pension ?? 0) };
        const setAsideFor = (label: string, metric: keyof typeof metricOf, leader: Strategy | null | undefined) => {
            const bestFlagged = fullUniverse.filter(flagged).reduce<Strategy | null>((b, s) => (b == null || metricOf[metric](s) > metricOf[metric](b) ? s : b), null);
            if (!bestFlagged || !leader || metricOf[metric](bestFlagged) <= metricOf[metric](leader)) return;
            const warning = engineStrategyHorizonWarning(bestFlagged, factFind);
            if (warning) horizonSetAside.push({ label, strategy: bestFlagged, warning, metric });
        };
        setAsideFor('Optimal Legacy', 'legacy', bestLegacy);
        setAsideFor('Optimal Cashflow', 'cashflow', bestCashflow);
        setAsideFor('Optimal Age Pension', 'pension', bestPension);
        const hasPensionEligibility = (bestPension?.snapshot?.pension ?? 0) > 0;
        const used = new Set<string | undefined>();
        const add = (strategy: Strategy | null | undefined, label: string) => {
            if (!strategy || used.has(strategy.name)) return false;
            used.add(strategy.name);
            recommendedOptimalStrategies.push({ label, strategy });
            return true;
        };
        add(bestLegacy, 'Optimal Legacy');
        if (!add(bestCashflow, 'Optimal Cashflow')) {
            const nextCashflow = recommendationUniverse
                .filter(s => !used.has(s.name))
                .reduce<Strategy | null>((best, s) => (s.snapshot?.netCashflow ?? -Infinity) > (best?.snapshot?.netCashflow ?? -Infinity) ? s : best, null);

            // If the best cashflow strategy is already used (same as legacy), keep it as
            // "Optimal Cashflow" unless an alternative is at least as good.
            if (nextCashflow && (nextCashflow.snapshot?.netCashflow ?? -Infinity) >= (bestCashflow?.snapshot?.netCashflow ?? -Infinity)) {
                add(nextCashflow, 'Optimal Cashflow');
            } else if (bestCashflow) {
                recommendedOptimalStrategies.push({ label: 'Optimal Cashflow', strategy: bestCashflow });
            }
        }
        if (hasPensionEligibility) {
            if (!add(bestPension, 'Optimal Age Pension')) {
                const nextPension = recommendationUniverse
                    .filter(s => !used.has(s.name))
                    .reduce<Strategy | null>((best, s) => (s.snapshot?.pension ?? 0) > (best?.snapshot?.pension ?? 0) ? s : best, null);
                if (nextPension) add(nextPension, 'Optimal Age Pension');
            }
        } else {
            const nextLegacy = eligibleStrategies.find(s => !used.has(s.name)) ?? strategies.find(s => !used.has(s.name));
            if (nextLegacy) add(nextLegacy, 'Optimal Age Pension');
        }
        /*
         * THE FOURTH GOAL, FIRST (owner, 24 Sep 2026): the best estate among the plans that pay the
         * bills; failing that, whose shortfall the savings carry for the whole period; failing that,
         * the one that lasts longest, badged as such. In care a daily payment on the room nearly
         * always leaves a shortfall, so the second case is the common one here — and it is the
         * question the family is actually asking. A flagged strategy cannot be crowned here either.
         */
        const covered = pickCashflowCoveredLeader(recommendationUniverse.length ? recommendationUniverse : fullUniverse, { cashflowTarget: 0 });
        if (covered) recommendedOptimalStrategies.unshift({ label: CASHFLOW_COVERED_LABEL, strategy: covered.strategy, cashflowCovered: covered.verdict });
    }

    // Check for 70% RAD strategies specifically
    const seventyPercentStrategies = strategies.filter(s => (s.name || '').includes('70% RAD'));
    if (seventyPercentStrategies.length > 0) {
    } else {
        console.warn('Ã¢Å¡Â Ã¯Â¸Â NO 70% RAD STRATEGIES FOUND IN RESULTS! Checking all strategy names...');
    }
    
    const result = {
        strategies: strategies,
        allSimulatedStrategies: allSimulatedStrategies,
        totalScenarios: scenarios.length,
        processedScenarios: strategies.length,
        recommendedOptimalStrategies: recommendedOptimalStrategies,
        horizonSetAside,
        futureEventV2: futureEventConfig.enabled
            ? {
                enabled: true,
                rankingMetric: 'baseline_legacy',
                fourthCardMetric: 'future_adjusted_legacy',
                assumptions: {
                    partnerEntryYear: futureEventConfig.partnerEntryYear,
                    partnerRoomPrice: futureEventConfig.partnerRoomPrice,
                    originalEntrantAliveAtEvent: futureEventConfig.originalEntrantAliveAtEvent,
                },
            }
            : {
                enabled: false,
                rankingMetric: 'legacy',
                assumptions: null,
            },
    };
    
    return rememberAnalysis(cacheKey, result);
}

// Generate scenarios directly in UI
/**
 * The RAD levels a debt-payoff twin is generated for.
 *
 * NOT every level, deliberately, and this is a real cap rather than an oversight. Pairing all 19 would
 * double a scenario set that is already the slowest part of the engine, and it would buy almost nothing:
 * whether clearing a debt beats holding it barely interacts with the RAD percentage, because both uses
 * of a dollar shelter it from the assets test equally and the difference is the loan's interest rate
 * against MPIR. A spread across the range gives the ranking both worlds to choose between at a fraction
 * of the cost. If a debt-payoff card ever wins at one level and loses at its neighbour, widen this.
 */
const DEBT_PAYOFF_PAIRED_RAD_LEVELS = [0, 25, 50, 75, 100];

/** Debts worth generating a payoff candidate for, and what they would cost to clear. */
function materialDebtsForSuggestionEngine(factFind: DraftFactFind) {
    // Below this a payoff cannot move a ranking — the assets-test taper on $5,000 is $390 a year — and
    // each candidate it generates costs a full projection to evaluate.
    const MATERIALITY_FLOOR = 5_000;
    const debts: PayableDebt[] = payableDebtsOf(factFind);
    const total = debts.reduce((sum, debt) => sum + debt.balance, 0);
    return total >= MATERIALITY_FLOOR ? debts : [];
}

/**
 * Pairs a spread of the generated scenarios with a twin that also clears the client's debts.
 *
 * The engine is given both worlds and its existing ranking picks — there is no debt-specific rule, and
 * deliberately so. "Should this be paid off?" is answered by the same legacy-and-cashflow comparison
 * that answers every other question here, which means there is no second rulebook to keep in step with
 * the first. It also means the answer can differ per client, which it genuinely does: an unsecured debt
 * counts against the assets test in full, a mortgage over an investment property is already netted off
 * that property, and one over an exempt home does nothing for the means test at all.
 *
 * Funded from the client's own cash first and only then from other liquid assets, so a payoff candidate
 * does not gratuitously trigger CGT that would make it lose for the wrong reason.
 */
function appendDebtPayoffScenarios(scenarios: RadFundingScenario[], factFind: DraftFactFind): void {
    const debts = materialDebtsForSuggestionEngine(factFind);
    if (debts.length === 0) return;

    const payments = Object.fromEntries(debts.map((debt) => [debt.key, debt.balance]));
    const totalToClear = debts.reduce((sum, debt) => sum + debt.balance, 0);

    const liquid = getPersonalLiquidAssetsForRad(factFind);
    // Cash first — it carries no CGT and no disposal decision.
    const ordered = [...liquid].sort((a, b) => Number(b.typeId === 'bankAccounts') - Number(a.typeId === 'bankAccounts'));

    const paired = scenarios.filter((scenario) => {
        const match = /Pay (\d+)% RAD/.exec(String(scenario.name || ''));
        if (!match) return String(scenario.name || '') === 'Do Nothing';
        return DEBT_PAYOFF_PAIRED_RAD_LEVELS.includes(Number(match[1]));
    });

    for (const base of paired) {
        /**
         * What THIS candidate's deposit has already claimed of each asset.
         *
         * The bug this fixes, and it invalidated every paired RAD candidate. The payoff's funding used
         * to be computed once from raw balances and attached to all of them, so a "Pay 60% RAD + Clear
         * Debt" candidate allocated the same bank account twice. The engine clears debt at order 3 and
         * sells for the deposit at order 4, so the debt claimed first and the DEPOSIT WAS STARVED —
         * that candidate quietly paid a smaller RAD than its own name promised, and scored accordingly.
         *
         * Only the no-RAD pairing was unaffected, because it allocates nothing. Which is exactly why
         * "Do Nothing + Clear Debt" kept winning: it was the one paired candidate not being sabotaged.
         *
         * The pool is shared, so the payoff now spends only what the deposit left — the same rule the
         * Manual Control cards enforce with their sliders.
         */
        const claimedByRad = flattenSeRadFundingToManualKeys(base.radFunding ?? {});

        const funding: Record<string, number> = {};
        let stillToRaise = totalToClear;
        for (const asset of ordered) {
            if (stillToRaise <= 0) break;
            const room = Math.max(0, (Number(asset.value) || 0) - (Number(claimedByRad[asset.assetId]) || 0));
            const take = Math.min(stillToRaise, room);
            if (take <= 0) continue;
            funding[asset.assetId] = take;
            stillToRaise -= take;
        }
        // This candidate's deposit has taken everything: there is nothing left to clear a debt with, so
        // there is no choice to model. Skipped rather than generated as a payoff that cannot happen.
        const affordable = Object.values(funding).reduce((sum, amount) => sum + amount, 0);
        if (affordable <= 0) continue;

        /**
         * The payoff variants worth modelling for this candidate.
         *
         * Where the whole debt can be funded from what is left, one variant: clear it. The repayment
         * basis is moot, because a debt cleared outright has no repayment left to decide about.
         *
         * Where it cannot, the payoff is necessarily PARTIAL — and then the basis is a real fork with
         * no universal winner, so both are modelled and the ranking picks.
         */
        const canClearInFull = affordable >= totalToClear - 0.5;
        const partial: Record<string, number> = {};
        if (!canClearInFull) {
            let left = affordable;
            for (const debt of debts) {
                if (left <= 0) break;
                const take = Math.min(left, debt.balance);
                partial[debt.key] = take;
                left -= take;
            }
        }
        const variants: { suffix: string; payoff: Record<string, unknown> }[] = canClearInFull
            ? [{ suffix: '+ Clear Debt', payoff: { payments, funding } }]
            : [
                { suffix: '+ Pay Down Debt (keep repayments)', payoff: { payments: partial, funding, repaymentBasis: 'keep_repayment' } },
                { suffix: '+ Pay Down Debt (lower repayments)', payoff: { payments: partial, funding, repaymentBasis: 'lower_repayment' } },
            ];

        for (const variant of variants) {
            scenarios.push({
                ...base,
                name: `${base.name} ${variant.suffix}`,
                strategyOptions: {
                    ...base.strategyOptions,
                    debtPayoff: variant.payoff,
                },
            });
        }
    }
}

/**
 * "$50,000 from AMP BANK" — the assets this strategy sells to clear debt, or null where it clears none.
 *
 * The "Allocation to RAD" grid beside this is exactly what its heading says: money going to the
 * accommodation deposit. A debt payoff is not that, so it does not appear there — which left an adviser
 * looking at "AMP BANK — Kept: $150,000" on a card whose own badge said it cleared a $50,000 debt, with
 * nothing on screen reconciling the two. The projection was right the whole time; the card just never
 * mentioned the second withdrawal.
 */
function debtPayoffFundingLine(strategy: Strategy, factFind: DraftFactFind): string | null {
    const funding = (strategy.strategyOptions as { debtPayoff?: { funding?: Record<string, number> } } | undefined)?.debtPayoff?.funding;
    if (!funding) return null;
    const parts: string[] = [];
    for (const [key, amount] of Object.entries(funding)) {
        const value = Number(amount) || 0;
        if (value <= 0) continue;
        const parsed = parseRadFundingAssetKey(key);
        const asset = parsed ? (factFind.assets?.[parsed.assetType] || []).find((row) => String(row?.id ?? '') === String(parsed.assetId)) : null;
        parts.push(`${formatCurrency(Math.round(value))} from ${asset?.description || parsed?.assetType || 'your assets'}`);
    }
    if (parts.length === 0) return null;
    return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * "the Personal Loan ($50,000)" — what this strategy pays out, or null where it pays out nothing.
 *
 * Read off the strategy's own options rather than a flag set at generation time, so a candidate that
 * reached the card by any route still describes itself correctly.
 */
function debtsClearedBy(strategy: Strategy): string | null {
    const payments = (strategy.strategyOptions as { debtPayoff?: { payments?: Record<string, number> } } | undefined)?.debtPayoff?.payments;
    if (!payments) return null;
    const total = Object.values(payments).reduce((sum, amount) => sum + (Number(amount) || 0), 0);
    if (total <= 0) return null;
    const count = Object.keys(payments).length;
    const basis = (strategy.strategyOptions as { debtPayoff?: { repaymentBasis?: string } } | undefined)?.debtPayoff?.repaymentBasis;
    // Named only where the strategy carries it AND the name says this is a pay-DOWN, since the basis is
    // meaningless once a debt is cleared outright.
    const note = /Pay Down Debt/.test(String(strategy.name || '')) && basis
        ? basis === 'lower_repayment' ? ', lowering the repayment' : ', keeping the repayment'
        : '';
    return `${count === 1 ? 'debt' : `${count} debts`} totalling ${formatCurrency(Math.round(total))}${note}`;
}

function generateScenarios(
    factFind: DraftFactFind,
    agedCareCosts: AgedCareCosts,
    cleanCoupleValidation: CoupleValidation = { partnerAnnualRent: 40000 },
    /** A provider's quoted annual annuity income, where the client has entered one. */
    annuityIncomeOverride: number | null = null,
    /** The second room, where both of a couple enter care: each candidate pays the same share of it up front. */
    secondRoom: SecondRoomForEngine | null = null,
    /** A change of home: "stay put" is the comparison; the refund of the current deposit funds the new room first. */
    facilityMove: FacilityMoveForEngine | null = null,
    /** Leaving a village: the exit payment funds the room first when it arrives; only the rest is sold at entry. */
    villageExit: VillageExitForEngine | null = null,
): RadFundingScenario[] {
    const scenarios: RadFundingScenario[] = [];
    const secondTargetFor = (percentage: number) => (secondRoom ? { secondRadTarget: Math.round(secondRoom.roomPrice * (percentage / 100)) } : {});
    const radAmount = Number((agedCareCosts && agedCareCosts.radAmount) ? agedCareCosts.radAmount : 500000); // Use actual room cost from agedCareCosts


    // Baseline scenario (Do Nothing) - Full DAP + Daily Fees
    // DAP = Room Cost * MPIR + Daily Fees (MPIR updated quarterly)
    const MPIR = AGED_CARE_SCHEDULE.MPIR;
    const doNothingDap = radAmount * MPIR + Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365 + Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365;
    scenarios.push({
        name: facilityMove ? 'Stay put' : "Do Nothing",
        ...(facilityMove ? { stayPut: true } : {}),
        radFunding: {},
        ...secondTargetFor(0),
        strategyOptions: {
            annualDap: facilityMove ? facilityMove.currentDapAnnual : doNothingDap,
            rentHome: false,
            downsizeHome: false,
            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
        }
    });
    
    // Comprehensive RAD testing: 10% to 100% in 5% increments
    // This ensures we find the actuarially optimal RAD level
    const radPercentages = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];

    /**
     * Funding order + the total the variant can ACTUALLY raise, so a level whose request exceeds the
     * client's fundable assets is recognised as a duplicate of the level where the cap first bit.
     *
     * Without this, a client with $455,000 fundable against a $700,000 room generated "Pay 65%" through
     * "Pay 100%" as EIGHT candidates that all fund $455,000 — identical projections wearing different
     * names. The table showed rows with the same estate to the dollar (which an adviser rightly read as
     * a bug), the ranking evaluated the same strategy eight times, and a card could claim a percentage
     * it does not pay.
     */
    const seenFundedTotals = new Set<string>();

    for (const percentage of radPercentages) {
        const radAmountForThisLevel = radAmount * (percentage / 100);
        const dapAmount = (radAmount - radAmountForThisLevel) * MPIR +
                         Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365 +
                         Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365;

        // Phase 2: All funding order permutations for true optimisation
        // A change of home: only what the refund of the current deposit does not cover is sold.
        const toRaise = facilityMove
            ? Math.max(0, radAmountForThisLevel - facilityMove.refundToday)
            : villageExit
                ? Math.max(0, radAmountForThisLevel - villageExit.entitlement)
                : radAmountForThisLevel;
        const fundingVariants = toRaise > 0 ? generateAllFundingOrderStrategies(factFind, toRaise) : [{ radFunding: {}, orderLabel: facilityMove ? 'Refund only' : 'Village money only' }];

        for (let i = 0; i < fundingVariants.length; i++) {
            const { radFunding, orderLabel } = fundingVariants[i];
            const fundedTotal = Object.values(radFunding ?? {})
                .flatMap((entries) => (Array.isArray(entries) ? entries : entries ? [entries] : []))
                .reduce((sum, entry) => sum + (Number((entry as { amount?: number }).amount) || 0), 0);
            // Keeps the FIRST level that PAYS a given deposit — the lowest percentage, which is the one
            // whose name still tells the truth about what is paid. Keyed on the deposit, not the savings
            // sold: a village payout or a current room's refund tops a level up without a sale
            // (radLevelKey.ts; owner-caught, 9 Oct 2026).
            const topUp = facilityMove ? Math.max(0, Number(facilityMove.refundToday) || 0) : villageExit ? Math.max(0, Number(villageExit.entitlement) || 0) : 0;
            const fundedKey = radLevelDedupeKey({ orderLabel, level: radAmountForThisLevel, fundedTotal, topUp });
            if (seenFundedTotals.has(fundedKey)) continue;
            seenFundedTotals.add(fundedKey);
            const strategySuffix = fundingVariants.length > 1 ? ` (${orderLabel})` : '';
            const groupKeyKeep = `${percentage}-Keep`;
            const groupKeyRent = `${percentage}-Rent`;
            
            // "Keep Home" only where there is a home to keep: a village or granny-flat resident, or a
            // renter, has none, and the words misled (owner-caught, 9 Oct 2026).
            const keepHomeLabel = anyoneOwnsHome(factFind as unknown as FactFind) ? ', Keep Home' : '';
            // Keep Home scenario
            scenarios.push({
                name: `Pay ${percentage}% RAD${keepHomeLabel}${strategySuffix}`,
                radFunding,
                ...secondTargetFor(percentage),
                groupKey: groupKeyKeep,
                fundingOrderLabel: orderLabel,
                strategyOptions: {
                    annualDap: dapAmount,
                    rentHome: false,
                    downsizeHome: false,
                    scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                }
            });
            
            // Rent Home scenario — only where there is a home to let (householdPosition.ts). A renter,
            // a village resident or someone living with family used to get candidates renting out a
            // home that does not exist, at $1,000 a week (owner's review, 7 Oct 2026).
            if (!homeCanBeLet(factFind as unknown as FactFind)) continue;
            const rentAmount = factFind.home?.likelyRent || 1000;
            const rentExpenses = factFind.home?.likelyExpenses || 10000;
            const scenarioLivingExpenses = scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0);
            
            scenarios.push({
                name: `Pay ${percentage}% RAD, Rent Home${strategySuffix}`,
                radFunding,
                ...secondTargetFor(percentage),
                groupKey: groupKeyRent,
                fundingOrderLabel: orderLabel,
                strategyOptions: {
                    annualDap: dapAmount,
                    rentHome: true,
                    downsizeHome: false,
                    scenarioLivingExpenses: scenarioLivingExpenses,
                    rentAmount: rentAmount,
                    rentExpenses: rentExpenses
                }
            });
        }
    }
    
    // Comprehensive downsizing scenarios - All RAD levels tested with downsizing
    // Include when downsizeNewHomeValue is set (e.g. from suggestion engine settings)
    /*
     * BOTH IN CARE TOGETHER (owner, 7 Oct 2026): nobody will live in a new home, so "downsize" is
     * not a strategy for them - SELLING the home is. The same block runs with no new home (the
     * whole net proceeds are the surplus), named "Sell home", and without the "rent the home"
     * twins: there is no longer a home to let. Keep and rent-out candidates still run as before.
     * Where the second person enters LATER the home still houses them, so downsizing stays.
     */
    const saleOnly = (factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for === 'both' && !!secondRoom && !secondRoom.entersLater;
    const saleLabel = saleOnly ? 'Sell home' : 'Downsize';
    const downsizedHomeValue = saleOnly ? 0 : (cleanCoupleValidation.downsizeNewHomeValue || 250000);
    const downsizeEnabled = saleOnly
        ? parseFloat(String(factFind.home?.value || 0)) > 0
        : (cleanCoupleValidation.downsizeNewHomeValue || 0) > 0 && downsizedHomeValue > 0;
    
    if (downsizeEnabled) {
        const originalHomeValue = parseFloat(String(factFind.home?.value || 0));
        const sellingCosts = parseFloat(String(cleanCoupleValidation.sellingCosts)) || 0;
        // The duty on the new home is a sunk cost of the changeover like the selling costs, so it
        // comes off before the surplus is sized (owner, 23 Sep 2026). Zero where no state was chosen.
        const stampDuty = saleOnly ? 0 : parseFloat(String(cleanCoupleValidation.stampDuty)) || 0;
        const newHomeState = saleOnly ? undefined : typeof cleanCoupleValidation.newHomeState === 'string' ? cleanCoupleValidation.newHomeState : undefined;
        const netProceeds = Math.max(0, originalHomeValue - sellingCosts - stampDuty);
        const surplusFromDownsize = Math.max(0, netProceeds - downsizedHomeValue); // Money left over after buying new home Ã¢â€ â€™ assume goes to RAD first
        
        for (const percentage of radPercentages) {
            const radAmountForThisLevel = radAmount * (percentage / 100);
            const dapAmount = (radAmount - radAmountForThisLevel) * MPIR +
                             Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365 +
                             Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365;
            
            // Surplus from downsize goes to RAD first; shortfall funded from other assets (same sell-down options as non-downsize)
            const radAllocationFromSurplus = Math.min(surplusFromDownsize, radAmountForThisLevel);
            const shortfall = Math.max(0, radAmountForThisLevel - radAllocationFromSurplus);
            
            const fundingVariantsDownsize = shortfall > 0
                ? generateAllFundingOrderStrategies(factFind, shortfall)
                : [{ radFunding: {}, orderLabel: 'Surplus only' }];
            
            for (let i = 0; i < fundingVariantsDownsize.length; i++) {
                const { radFunding, orderLabel } = fundingVariantsDownsize[i];
                const strategySuffix = fundingVariantsDownsize.length > 1 ? ` (${orderLabel})` : '';
                const groupKeyDownKeep = `D${percentage}-Keep`;
                const groupKeyDownRent = `D${percentage}-Rent`;
                
                const downsizeDetails = {
                    newHomePurchasePrice: downsizedHomeValue,
                    radAllocation: radAllocationFromSurplus,
                    ...(stampDuty > 0 ? { stampDuty } : {}),
                    ...(newHomeState ? { newHomeState } : {}),
                };
                const strategyOptionsWithSellingCost = { sellingCosts };
                
                scenarios.push({
                    name: `${saleLabel} + ${percentage}% RAD${strategySuffix}`,
                    radFunding,
                    groupKey: groupKeyDownKeep,
                    fundingOrderLabel: orderLabel,
                    strategyOptions: {
                        annualDap: dapAmount,
                        rentHome: false,
                        downsizeHome: true,
                        downsizeDetails,
                        sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                        scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                    }
                });
                
                if (!saleOnly) scenarios.push({
                    name: `${saleLabel} + ${percentage}% RAD, Rent Home${strategySuffix}`,
                    radFunding,
                    groupKey: groupKeyDownRent,
                    fundingOrderLabel: orderLabel,
                    strategyOptions: {
                        annualDap: dapAmount,
                        rentHome: true,
                        downsizeHome: true,
                        downsizeDetails,
                        sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                        scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0),
                        rentAmount: factFind.home?.likelyRent || 1000,
                        rentExpenses: factFind.home?.likelyExpenses || 10000
                    }
                });

                const surplusAfterRad = Math.max(0, surplusFromDownsize - radAllocationFromSurplus);
                const clientAge = calculateAge(factFind.client?.dob) || 0;
                const partnerAge = factFind?.adviceFor === 'couple' ? (calculateAge(factFind.partner?.dob) || 0) : 0;

                const buildDownsizerDetails = (commenceIncomeStream: boolean) => {
                    let remaining = surplusAfterRad;
                    let downsizerContribution = 0;
                    let partnerDownsizerContribution = 0;
                    if (clientAge >= DOWNSIZER_MIN_AGE) {
                        downsizerContribution = Math.min(DOWNSIZER_CONTRIBUTION_CAP, remaining);
                        remaining -= downsizerContribution;
                    }
                    if (factFind?.adviceFor === 'couple' && partnerAge >= DOWNSIZER_MIN_AGE) {
                        partnerDownsizerContribution = Math.min(DOWNSIZER_CONTRIBUTION_CAP, remaining);
                    }
                    if (downsizerContribution + partnerDownsizerContribution < 10000) return null;
                    return {
                        newHomePurchasePrice: downsizedHomeValue,
                        radAllocation: radAllocationFromSurplus,
                        downsizerContribution,
                        partnerDownsizerContribution,
                        commenceIncomeStream: commenceIncomeStream === true,
                        ...(stampDuty > 0 ? { stampDuty } : {}),
                        ...(newHomeState ? { newHomeState } : {}),
                    };
                };

                const downsizerSuperDetails = isDownsizerContributionEligible(asFactFind(factFind), 'client')
                    || (factFind?.adviceFor === 'couple' && isDownsizerContributionEligible(asFactFind(factFind), 'partner'))
                    ? buildDownsizerDetails(false)
                    : null;

                if (downsizerSuperDetails) {
                    const suffixDownsizer = `${strategySuffix} + max downsizer`;
                    scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD${suffixDownsizer}`,
                        radFunding,
                        groupKey: `${groupKeyDownKeep}-DS`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: false,
                            downsizeHome: true,
                            downsizeDetails: downsizerSuperDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                        }
                    });
                    if (!saleOnly) scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD, Rent Home${suffixDownsizer}`,
                        radFunding,
                        groupKey: `${groupKeyDownRent}-DS`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: true,
                            downsizeHome: true,
                            downsizeDetails: downsizerSuperDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0),
                            rentAmount: factFind.home?.likelyRent || 1000,
                            rentExpenses: factFind.home?.likelyExpenses || 10000
                        }
                    });
                }

                const downsizerAbpDetails = (canCommenceAbpFromDownsizer(asFactFind(factFind), 'client')
                    || (factFind?.adviceFor === 'couple' && canCommenceAbpFromDownsizer(asFactFind(factFind), 'partner')))
                    ? buildDownsizerDetails(true)
                    : null;

                if (downsizerAbpDetails) {
                    const suffixAbp = `${strategySuffix} + downsizer ABP`;
                    scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD${suffixAbp}`,
                        radFunding,
                        groupKey: `${groupKeyDownKeep}-ABP`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: false,
                            downsizeHome: true,
                            downsizeDetails: downsizerAbpDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses)
                        }
                    });
                    if (!saleOnly) scenarios.push({
                        name: `${saleLabel} + ${percentage}% RAD, Rent Home${suffixAbp}`,
                        radFunding,
                        groupKey: `${groupKeyDownRent}-ABP`,
                        fundingOrderLabel: orderLabel,
                        strategyOptions: {
                            annualDap: dapAmount,
                            rentHome: true,
                            downsizeHome: true,
                            downsizeDetails: downsizerAbpDetails,
                            sellingCosts: strategyOptionsWithSellingCost.sellingCosts,
                            scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses) + (parseFloat(String(cleanCoupleValidation.partnerAnnualRent)) || 0),
                            rentAmount: factFind.home?.likelyRent || 1000,
                            rentExpenses: factFind.home?.likelyExpenses || 10000
                        }
                    });
                }
            }
        }
    }

    appendEntityIpSaleScenarios(scenarios, factFind, {
        radAmount,
        agedCareCosts,
        cleanCoupleValidation,
        MPIR,
    });

    /*
     * Borrowing against the home, for the families who do not want to sell it. The engine could
     * only ever answer "sell it, rent it, or pay a smaller deposit" — see
     * agedCareEquityReleaseCandidates.ts for the two schemes and why they are not interchangeable.
     */
    appendEquityReleaseScenarios(scenarios, factFind, {
        radAmount,
        otherAnnualFees:
            Number((agedCareCosts && agedCareCosts.basicDailyFee) ? agedCareCosts.basicDailyFee : 70) * 365
            + Number((agedCareCosts && agedCareCosts.dailyHSC !== undefined) ? agedCareCosts.dailyHSC : 0) * 365 + Number(agedCareCosts?.dailyMTCF ?? 0) * 365,
        MPIR,
        scenarioLivingExpenses: scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses),
    });

    /*
     * The three means-test levers, layered onto the deposit levels above. These are what an adviser
     * reaches for first in a residential care case and the engine had none of them — see
     * agedCareMeansTestCandidates.ts for what each one does and why the amounts are what they are.
     */
    appendMeansTestStrategyScenarios(scenarios, factFind, annuityIncomeOverride);

    // Last, so every scenario above is available to pair against.
    appendDebtPayoffScenarios(scenarios, factFind);

    // The row's target, where money arriving later (a refund, the village's payment) tops it up.
    if (facilityMove || villageExit) {
        for (const s of scenarios) {
            if (s.stayPut) continue;
            const m = /(\d+)%\s+RAD/i.exec(s.name || '');
            if (m) s.radTarget = Math.round(radAmount * (Number(m[1]) / 100));
        }
    }
    return scenarios;
}

function calculateRadFundingForAmount(factFind: DraftFactFind, amount: number) {
    return calculateSeRadFundingForAmount(factFind, amount);
}

interface RadFundingAvailableAsset {
    type: 'bankAccounts' | 'accountBasedPensions' | 'shares';
    id?: number | string;
    description?: string;
    value: number;
    growthRate: number;
    incomeRate: number;
    fullValue: number;
}

/**
 * Generate multiple RAD funding strategies for actuarial optimisation
 * Tests different asset combinations to maximize final net worth
 * @param {Object} factFind - Client's form data with assets and constraints
 * @param {number} amount - Total RAD amount to fund
 * @returns {Array} Array of radFunding objects representing different funding strategies
 */
function generateSmartRadFundingStrategies(factFind: DraftFactFind, amount: number) {
    const strategies: Record<string, { id?: number | string; amount: number; strategyName: string }[]>[] = [];
    const EMERGENCY_FUND_LIMIT = 50000; // $50K emergency fund protection

    // Collect available assets with their characteristics
    const availableAssets: RadFundingAvailableAsset[] = [];

    // Bank accounts
    if (factFind.assets?.bankAccounts) {
        factFind.assets.bankAccounts.forEach(asset => {
            const value = parseFloat(String(asset.value)) || 0;
            const growthRate = parseFloat(String(asset.growthRate)) || 2;
            const incomeRate = parseFloat(String(asset.incomeRate)) || 2;
            const available = Math.max(0, value - EMERGENCY_FUND_LIMIT); // Protect emergency fund

            if (available > 0) {
                availableAssets.push({
                    type: 'bankAccounts',
                    id: asset.id,
                    description: asset.description,
                    value: available,
                    growthRate: growthRate,
                    incomeRate: incomeRate,
                    fullValue: value
                });
            }
        });
    }

    // Account-based pensions
    if (factFind.assets?.accountBasedPensions) {
        factFind.assets.accountBasedPensions.forEach(asset => {
            const value = parseFloat(String(asset.value)) || 0;
            const growthRate = parseFloat(String(asset.growthRate)) || 5;
            const incomeRate = 0; // Income is based on minimum drawdown rules

            if (value > 0) {
                availableAssets.push({
                    type: 'accountBasedPensions',
                    id: asset.id,
                    description: asset.description,
                    value: value,
                    growthRate: growthRate,
                    incomeRate: incomeRate,
                    fullValue: value
                });
            }
        });
    }

    // Shares
    if (factFind.assets?.shares) {
        factFind.assets.shares.forEach(asset => {
            const value = parseFloat(String(asset.value)) || 0;
            const growthRate = parseFloat(String(asset.growthRate)) || 5;
            const incomeRate = parseFloat(String(asset.incomeRate)) || 4;

            if (value > 0) {
                availableAssets.push({
                    type: 'shares',
                    id: asset.id,
                    description: asset.description,
                    value: value,
                    growthRate: growthRate,
                    incomeRate: incomeRate,
                    fullValue: value
                });
            }
        });
    }
    
    // Single strategy: Bank accounts, then ABPs, then shares (optimal order considering CGT and growth)
    // Bank accounts have no CGT and minimal growth impact
    // ABPs provide income and are tax-free
    // Shares have CGT and moderate growth
    const optimalSort = [...availableAssets].sort((a, b) => {
        // Prioritize bank accounts first (no CGT, minimal income/growth impact)
        if (a.type === 'bankAccounts' && b.type !== 'bankAccounts') return -1;
        if (b.type === 'bankAccounts' && a.type !== 'bankAccounts') return 1;
        // Then ABPs (tax-free, income-bearing) - liquidate lowest growth first to preserve higher growth
        if (a.type === 'accountBasedPensions' && b.type !== 'accountBasedPensions') return -1;
        if (b.type === 'accountBasedPensions' && a.type !== 'accountBasedPensions') return 1;
        if (a.type === 'accountBasedPensions' && b.type === 'accountBasedPensions') {
            // Within ABPs, liquidate lowest growth first
            return a.growthRate - b.growthRate;
        }
        // Then shares (last resort due to CGT)
        return 0;
    });
    const optimalStrategy = generateSingleFundingStrategy(optimalSort, amount, 'Optimal');
    if (optimalStrategy) strategies.push(optimalStrategy);
    
    // Fallback: If no strategies generated, return original simple approach
    if (strategies.length === 0) {
        return [calculateRadFundingForAmount(factFind, amount)];
    }
    
    return strategies;
}

/**
 * Generate a single funding strategy from sorted assets
 * @param {Array} sortedAssets - Assets sorted by priority
 * @param {number} amount - Total amount to fund
 * @param {string} strategyName - Name of the strategy
 * @returns {Object|null} radFunding object or null if can't fund
 */
function generateSingleFundingStrategy(sortedAssets: RadFundingAvailableAsset[], amount: number, strategyName: string): Record<string, { id?: number | string; amount: number; strategyName: string }[]> | null {
    const radFunding: Record<string, { id?: number | string; amount: number; strategyName: string }[]> = {};
    let remainingAmount = amount;

    for (const asset of sortedAssets) {
        if (remainingAmount <= 0) break;

        const liquidateAmount = Math.min(remainingAmount, asset.value);

        if (liquidateAmount > 0) {
            if (!radFunding[asset.type]) radFunding[asset.type] = [];
            radFunding[asset.type]!.push({
                id: asset.id,
                amount: liquidateAmount,
                strategyName: strategyName
            });
            remainingAmount -= liquidateAmount;
        }
    }

    // Return null if we couldn't fund the full amount
    if (remainingAmount > 1000) { // Allow $1K tolerance for rounding
        return null;
    }

    return radFunding;
}

/**
 * Phase 2: Generate all funding strategies by trying every order of the client's asset types.
 * Used for full optimisation (accuracy over speed).
 * @returns {Array<{ radFunding: Object, orderLabel: string }>}
 */
function generateAllFundingOrderStrategies(factFind: DraftFactFind, amount: number) {
    return generateSeFundingVariants(factFind, amount);
}

/** Apply scenario RAD + optional entity IP sale (respects structure funding gate via collected assets). */
function applyRadFundingToFactFind(factFind: DraftFactFind, scenarioOrRadFunding: Strategy | Record<string, number | string> | null | undefined): DraftFactFind {
    const scenario: Record<string, unknown> =
        (scenarioOrRadFunding as Strategy)?.strategyOptions != null || (scenarioOrRadFunding as Strategy)?.name != null
            ? (scenarioOrRadFunding as Record<string, unknown>)
            : { radFunding: scenarioOrRadFunding || {}, strategyOptions: {} };
    return applySuggestionEngineScenarioToFactFind(factFind, scenario);
}


// Build tooltip data for a downsize strategy in the "All modelled strategies" table - explains exactly what the system does
function getDownsizeStrategyTooltipData(factFind: DraftFactFind, strategy: Strategy): CalculationTooltipItem[] | null {
    const opts = strategy.strategyOptions as { downsizeHome?: boolean; downsizeDetails?: Record<string, unknown>; sellingCosts?: unknown } | undefined;
    if (!opts?.downsizeHome || !opts?.downsizeDetails) return null;
    const dd = opts.downsizeDetails;
    const salePrice = parseFloat(String(factFind?.home?.value || 0));
    const sellingCost = parseFloat(String(opts.sellingCosts)) || 0;
    const stampDuty = parseFloat(String(dd.stampDuty)) || 0;
    const netProceeds = Math.max(0, salePrice - sellingCost - stampDuty);
    const newHomeCost = parseFloat(String(dd.newHomePurchasePrice)) || 0;
    const toRAD = parseFloat(String(dd.radAllocation)) || 0;
    const clientDownsizer = parseFloat(String(dd.downsizerContribution)) || 0;
    const partnerDownsizer = parseFloat(String(dd.partnerDownsizerContribution)) || 0;
    const surplus = Math.max(0, netProceeds - newHomeCost);
    const remainderToBank = Math.max(0, surplus - toRAD - clientDownsizer - partnerDownsizer);
    const data: CalculationTooltipItem[] = [
        { label: '--- Downsize: what the system does ---', value: '' },
        { label: 'Sale price (current home)', value: formatCurrency(salePrice) },
        { label: 'Less: Selling costs', value: '-' + formatCurrency(sellingCost) },
        // Its own line, so a reader sees the changeover's second sunk cost — or sees that it was not
        // estimated, which is as important (owner, 23 Sep 2026).
        ...(newHomeCost > 0
            ? [stampDuty > 0
                ? { label: 'Less: Stamp duty on new home', value: '-' + formatCurrency(stampDuty) }
                : { label: 'Stamp duty on new home', value: 'not estimated — no state chosen' }]
            : []),
        { label: 'Net proceeds', value: formatCurrency(netProceeds) },
        { label: 'Less: New home cost', value: '-' + formatCurrency(newHomeCost) },
        { label: 'Surplus (to RAD or bank)', value: formatCurrency(surplus) },
        { label: 'Amount to RAD (from surplus)', value: formatCurrency(toRAD) },
    ];
    if (clientDownsizer > 0) {
        data.push({
            label: dd.commenceIncomeStream ? 'Client downsizer to ABP' : 'Client downsizer to super',
            value: formatCurrency(clientDownsizer),
        });
    }
    if (partnerDownsizer > 0) {
        data.push({
            label: dd.commenceIncomeStream ? 'Partner downsizer to ABP' : 'Partner downsizer to super',
            value: formatCurrency(partnerDownsizer),
        });
    }
    data.push({ label: 'Remainder to bank', value: formatCurrency(remainderToBank) });
    const otherSellDowns = getRadFundingTooltipData(strategy.radFunding as unknown as RadFundingBag);
    if (otherSellDowns && otherSellDowns.length > 0) {
        data.push({ label: '--- Other sell downs (shortfall) ---', value: '' });
        otherSellDowns.forEach(ro => data.push({ label: ro.label, value: ro.value }));
    }
    return data;
}

export default AgedCareSuggestionEnginePanel;
